import moment from "moment";
import type { VueInspectorClient } from "vite-plugin-vue-inspector";
import { type Component, computed, markRaw, nextTick } from "vue";

import type { Actions } from "~/actions";
import { canRunEditorCommand, runEditorCommand } from "~/components/cmdCtr/editorCommands";
import { fuzzySearch } from "~/components/cmdCtr/fuzzySearch";
import { LAYOUT_KIND_TO_NEXT_LAYOUT_KIND, LAYOUT_KIND_TO_PREVIOUS_LAYOUT_KIND } from "~/constants/layout";
import { SIZE_FORMAT_TO_COLOR_MAP, TSHIRT_SIZE_TO_NUMBER_SIZE, UNSIZED_SIZE_LABEL } from "~/constants/size";
import { getAvailableViewModeTabs, VIEW_MODE_DIGIT_KEYS, VIEW_MODE_TAB_COMMAND_IDS } from "~/constants/viewMode";
import {
  AccountSettingsIcon,
  AgentIcon,
  ApiIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  AssigneeFieldIcon,
  AttachmentFieldIcon,
  AutomationsIcon,
  BlueskyIcon,
  BoardViewIcon,
  BoldIcon,
  CalendarIcon,
  CalendarViewIcon,
  CareersIcon,
  ChangelogIcon,
  ChatgptIcon,
  ChatIcon,
  CheckboxFieldIcon,
  ChecklistIcon,
  ChevronDownDoubleIcon,
  ChevronDownIcon,
  ChevronUpDoubleIcon,
  ChevronUpIcon,
  ClaudeIcon,
  CodeBlockIcon,
  CodeSnippetIcon,
  ColorPickerIcon,
  DartboardIcon,
  DashboardIcon,
  DescriptionFieldIcon,
  DesktopOrWebAppIcon,
  DiscordIcon,
  DocIcon,
  DueDateFieldIcon,
  EditIcon,
  ExportIcon,
  FilterIcon,
  FolderIcon,
  FormIcon,
  GitBranchIcon,
  GithubIcon,
  HelpCenterIcon,
  HomeIcon,
  HorizontalRuleIcon,
  IdentityIcon,
  ImportIcon,
  InboxIcon,
  IndentIcon,
  InstagramIcon,
  ItalicIcon,
  KeyboardShortcutsIcon,
  LargeHeaderIcon,
  LayoutIcon,
  LayoutLeftIcon,
  LinkedinIcon,
  LinkFieldIcon,
  LinkIcon,
  ListViewIcon,
  LogOutIcon,
  MailIcon,
  MediumHeaderIcon,
  MicrophoneIcon,
  MobileAppIcon,
  MultipleUsersFieldIcon,
  MyTasksIcon,
  NotificationIcon,
  NotionIcon,
  NumberedListIcon,
  OutdentIcon,
  PipedreamIcon,
  PlansAndBillingSettingsIcon,
  PomodoroIcon,
  PriorityFieldIcon,
  PriorityIcon,
  PrivacyPolicyIcon,
  ProjectIcon,
  PromptIcon,
  PropertiesIcon,
  QuoteBlockIcon,
  RecommendationIcon,
  RecommendSubtasksIcon,
  RedoIcon,
  RefreshIcon,
  ReminderIcon,
  ReplicateIcon,
  ReviewerFieldIcon,
  RightbarIcon,
  RoadmapViewIcon,
  SearchIcon,
  SelectAllIcon,
  SettingsIcon,
  SizeFieldIcon,
  SizeIcon,
  SlackIcon,
  SmallHeaderIcon,
  StandardTextIcon,
  StartDateFieldIcon,
  StatusFieldIcon,
  StatusWebsiteIcon,
  StrikethroughIcon,
  SubtasksCollapsedIcon,
  SubtasksIndentedIcon,
  TableChartIcon,
  TagsFieldIcon,
  TaskCreateIcon,
  TaskIcon,
  TermsOfUseIcon,
  ThemeIcon,
  ThreadsIcon,
  TiktokIcon,
  ToggleSectionIcon,
  TrashIcon,
  UnassignedIcon,
  UnderlineIcon,
  UndoIcon,
  UnorderedListIcon,
  UsersInviteIcon,
  ViewIcon,
  WebhooksIcon,
  WebsiteIcon,
  WorkflowViewIcon,
  WorkspaceSettingsIcon,
  XCompanyIcon,
  YoutubeIcon,
  ZapierIcon,
  ZohoFlowIcon,
} from "~/icons";
import { isDefined } from "~/shared/common";
import {
  CommandActionType,
  CommandId,
  Key,
  KeyboardShortcutStrokeKind,
  LayoutKind,
  PageKind,
  Placement,
  Priority,
  StatusKind,
  Subscription,
  SubtaskDisplayMode,
  Theme,
  TshirtSize,
  UserRole,
  ViewKind,
  ViewMode,
} from "~/shared/enums";
import { KeyDisplay } from "~/shared/keyboardKeys";
import type {
  Chat,
  Command,
  CommandActionMultiSelect,
  CommandActionSelect,
  CommandAvailabilityRule,
  CommandEntityList,
  CommandEntitySection,
  CommandSection,
  Dartboard,
  Dashboard,
  Doc,
  Folder,
  HelpCenterArticle,
  KeyboardShortcutStroke,
  Option,
  Shortcut,
  Status,
  Task,
  View,
} from "~/shared/types";
import { BlockedIcon, CanceledIcon, FinishedIcon, StartedIcon, UnstartedIcon } from "~/status-icons";
import type { DataStore } from "~/stores/DataStore";
import {
  getChatLink,
  getDartboardLink,
  getDashboardLink,
  getDocLink,
  getFolderLink,
  getPageDisplayName,
  getTaskLink,
  getViewLink,
} from "~/utils/common";
import { formatDate, parseDate } from "~/utils/date";
import { CHAT_NO_TITLE_LABEL } from "~/utils/entityTitle";
import { getRelativeTimeForDatesDate } from "~/utils/time";
import { getUserDisplayName } from "~/utils/user";
import { switchViewMode } from "~/utils/viewModeNavigation";

import { PRIORITY_COLOR_MAP } from "./priority";

let actions: Actions;
let Avatar: Component;
let ChatPreviewTooltip: Component;
let EntityPreviewTooltip: Component;
let PageIcon: Component;
let StatusIcon: Component;
export const initCommands = (
  actionsInstance: Actions,
  avatar: Component,
  chatPreviewTooltip: Component,
  entityPreviewTooltip: Component,
  pageIcon: Component,
  statusIcon: Component
) => {
  actions = actionsInstance;
  Avatar = avatar;
  ChatPreviewTooltip = chatPreviewTooltip;
  EntityPreviewTooltip = entityPreviewTooltip;
  PageIcon = pageIcon;
  StatusIcon = statusIcon;
};

// oxlint-disable-next-line unicorn/no-thenable
export const makeShortcut = (keys: Key[], then?: Key[]): Shortcut => ({ keys, then });

const OR = {
  kind: KeyboardShortcutStrokeKind.SEPARATOR,
  content: "or",
};

const asText = (content: string) => ({ kind: KeyboardShortcutStrokeKind.TEXT, content });
const asMarkdown = (content: string) => ({
  kind: KeyboardShortcutStrokeKind.MARKDOWN,
  content: content.replace(/^ /, "\xa0").replace(/ $/, "\xa0"),
});

const possibleAssigneeEntityList = (
  dataStore: DataStore
): CommandEntityList<{ key: string | null; label: string }> => ({
  name: "assignee",
  getEntitiesFn: async () => {
    const assignees = await actions.visualization.getPossibleAssignees();
    return assignees.map((e) => {
      const user = e.key ? dataStore.getUserByDuid(e.key) : undefined;
      return {
        value: e.key,
        label: e.label,
        aliases: [],
        icon: markRaw(user ? Avatar : UnassignedIcon),
        iconArgs: user ? { user } : undefined,
        entity: e,
      };
    });
  },
});

const assigneesEntityList = (dataStore: DataStore): CommandEntityList<{ key: string | null; label: string }> => ({
  name: "assignee",
  getEntitiesFn: async () => {
    const tasks = await actions.visualization.getCurrentRowsTasks();
    const currentAssignees = await actions.visualization.getCurrentAssignees();
    const currentAssigneeKeys = new Set(currentAssignees.map((e) => e.key));

    const allAssignees = dataStore.getRelevantUsersByTasks(tasks).map((e) => ({
      key: e.duid,
      label: getUserDisplayName(e),
    }));

    return allAssignees.map((e) => {
      const user = e.key ? dataStore.getUserByDuid(e.key) : undefined;
      return {
        value: e.key,
        label: e.label,
        aliases: [],
        icon: markRaw(user ? Avatar : UnassignedIcon),
        iconArgs: user ? { user } : undefined,
        entity: e,
        selected: e.key ? currentAssigneeKeys.has(e.key) : false,
      };
    });
  },
});

const possibleReviewerEntityList = (
  dataStore: DataStore
): CommandEntityList<{ key: string | null; label: string }> => ({
  name: "reviewer",
  getEntitiesFn: async () => {
    const reviewers = await actions.visualization.getPossibleReviewers();
    return reviewers.map((e) => {
      const user = e.key ? dataStore.getUserByDuid(e.key) : undefined;
      return {
        value: e.key,
        label: e.label,
        aliases: [],
        icon: markRaw(user ? Avatar : UnassignedIcon),
        iconArgs: user ? { user } : undefined,
        entity: e,
      };
    });
  },
});

const reviewersEntityList = (dataStore: DataStore): CommandEntityList<{ key: string | null; label: string }> => ({
  name: "reviewer",
  getEntitiesFn: async () => {
    const tasks = await actions.visualization.getCurrentRowsTasks();
    const currentReviewers = await actions.visualization.getCurrentReviewers();
    const currentReviewerKeys = new Set(currentReviewers.map((e) => e.key));

    const allReviewers = dataStore.getRelevantUsersByTasks(tasks).map((e) => ({
      key: e.duid,
      label: getUserDisplayName(e),
    }));

    return allReviewers.map((e) => {
      const user = e.key ? dataStore.getUserByDuid(e.key) : undefined;
      return {
        value: e.key,
        label: e.label,
        aliases: [],
        icon: markRaw(user ? Avatar : UnassignedIcon),
        iconArgs: user ? { user } : undefined,
        entity: e,
        selected: e.key ? currentReviewerKeys.has(e.key) : false,
      };
    });
  },
});

const taskEntityList = (dataStore: DataStore): CommandEntityList<Task> => ({
  name: "task",
  getEntitiesFn: async (query: string) => {
    const { data } = await dataStore.loadTasks({ title: query, isCompleted: false, inTrash: false }, 0, {
      limit: 10,
      key: "command-center-tasks",
    });
    return data.results.map((e) => {
      const status = dataStore.getStatusByDuid(e.statusDuid);
      const dartboard = dataStore.getDartboardByDuid(e.dartboardDuid);
      const space = dartboard ? dataStore.getSpaceByDuid(dartboard.spaceDuid) : undefined;
      return {
        value: e.duid,
        label: e.title,
        aliases: [status?.title, space?.title, space?.abrev, dartboard?.title].filter(isDefined),
        icon: markRaw(StatusIcon),
        iconArgs: { duid: e.statusDuid },
        entity: e,
      };
    });
  },
  tooltip: {
    component: markRaw(EntityPreviewTooltip),
    args: (e) => ({ taskDuid: e.duid, placement: Placement.RIGHT }),
  },
});

const dartboardEntityList = (dataStore: DataStore): CommandEntityList<Dartboard> => ({
  name: "dartboard",
  getEntitiesFn: async () =>
    actions.visualization.getPossibleDartboards().map((e) => {
      const space = dataStore.getSpaceByDuid(e.spaceDuid);
      return {
        value: e.duid,
        label: getPageDisplayName(e, dataStore.getSpaceByDuid, "abrev"),
        aliases: [space?.title, space?.abrev, e.description].filter(isDefined),
        icon: markRaw(PageIcon),
        iconArgs: { page: e },
        entity: e,
      };
    }),
});

const dashboardEntityList = (dataStore: DataStore): CommandEntityList<Dashboard> => ({
  name: "dashboard",
  getEntitiesFn: async () =>
    dataStore.dashboardList.map((e) => ({
      value: e.duid,
      label: e.title,
      aliases: [e.description],
      icon: markRaw(PageIcon),
      iconArgs: { page: e },
      entity: e,
    })),
});

const viewEntityList = (dataStore: DataStore): CommandEntityList<View> => ({
  name: "view",
  getEntitiesFn: async () =>
    dataStore.viewList
      .filter((e) => e.kind === ViewKind.CUSTOM)
      .map((e) => ({
        value: e.duid,
        label: e.title,
        aliases: [e.description],
        icon: markRaw(PageIcon),
        iconArgs: { page: e },
        entity: e,
      })),
});

const folderEntityList = (dataStore: DataStore): CommandEntityList<Folder> => ({
  name: "folder",
  getEntitiesFn: async () =>
    dataStore.getFolderList().map((e) => {
      const space = dataStore.getSpaceByDuid(e.spaceDuid);
      return {
        value: e.duid,
        label: getPageDisplayName(e, dataStore.getSpaceByDuid, "abrev"),
        aliases: [space?.title, space?.abrev, e.description].filter(isDefined),
        icon: markRaw(PageIcon),
        iconArgs: { page: e },
        entity: e,
      };
    }),
});

const helpCenterArticleEntityList = (dataStore: DataStore): CommandEntityList<HelpCenterArticle> => ({
  name: "help-center-article",
  getEntitiesFn: async (query: string) => {
    const { data } = await dataStore.$backendOld.entityList.helpCenterArticle({ q: query }, 0, {
      limit: 10,
      key: "command-center-help-articles",
    });
    return data.results.map((e) => ({
      value: e.url,
      label: e.title,
      aliases: [e.description],
      icon: markRaw(HelpCenterIcon),
      entity: e,
    }));
  },
});

const docEntityList = (dataStore: DataStore): CommandEntityList<Doc> => ({
  name: "doc",
  getEntitiesFn: async (query: string) => {
    const { data } = await dataStore.loadDocs({ title: query }, 0, { limit: 10, key: "command-center-docs" });
    return data.results.map((e) => {
      const folder = dataStore.getFolderByDuid(e.folderDuid);
      const space = folder ? dataStore.getSpaceByDuid(folder.spaceDuid) : undefined;
      return {
        value: e.duid,
        label: e.title,
        aliases: [folder?.title, space?.title, space?.abrev].filter(isDefined),
        icon: markRaw(DocIcon),
        iconArgs: { doc: e },
        entity: e,
      };
    });
  },
  tooltip: {
    component: markRaw(EntityPreviewTooltip),
    args: (e) => ({ docDuid: e.duid, placement: Placement.RIGHT }),
  },
});

const chatEntityList = (dataStore: DataStore): CommandEntityList<Chat> => ({
  name: "chat",
  getEntitiesFn: async (query: string) => {
    const { data } = await dataStore.loadChats({ search: query || undefined }, 0, 10);
    return data.results.map((e) => {
      const agent = e.targetUserDuid ? dataStore.getUserByDuid(e.targetUserDuid) : undefined;
      return {
        value: e.duid,
        label: e.title || CHAT_NO_TITLE_LABEL,
        icon: agent ? markRaw(Avatar) : undefined,
        iconArgs: agent ? { user: agent } : undefined,
        entity: e,
      };
    });
  },
  tooltip: {
    component: markRaw(ChatPreviewTooltip),
    args: (e) => ({ chat: e }),
  },
});

const statusEntityList = (): CommandEntityList<Status> => ({
  name: "status",
  getEntitiesFn: async () =>
    (await actions.visualization.getPossibleStatuses()).map((e) => ({
      value: e.duid,
      label: e.title,
      aliases: [],
      icon: markRaw(StatusIcon),
      iconArgs: { duid: e.duid },
      entity: e,
    })),
});

const getSelectedTasksDateConstraints = async () => {
  const tasks = await actions.visualization.getCurrentRowsTasks();
  const startDates = tasks.map((task) => (task.startAt ? moment(task.startAt).toDate() : null)).filter(isDefined);
  const dueDates = tasks.map((task) => (task.dueAt ? moment(task.dueAt).toDate() : null)).filter(isDefined);

  const minStartDate = startDates.length > 0 ? moment.max(startDates.map((d) => moment(d))).toDate() : undefined;
  const maxDueDate = dueDates.length > 0 ? moment.min(dueDates.map((d) => moment(d))).toDate() : undefined;

  return { minDate: minStartDate, maxDate: maxDueDate };
};

const dateEntityList = (
  getConstraints?: () => Promise<{ minDate?: Date; maxDate?: Date }>
): CommandEntityList<{ dateStr: string; displayName: string }> => ({
  name: "date",
  getEntitiesFn: async (query?: string) => {
    const { minDate, maxDate } = getConstraints ? await getConstraints() : {};

    const today = moment().startOf("day");
    const nextMonday = today.clone().day(today.day() === 0 ? 1 : 8);
    const firstOfNextMonth = today.clone().add(1, "month").startOf("month");
    const inTwoMonths = today.clone().add(2, "months");
    const defaultDates = [
      { dateStr: formatDate(today), displayName: "Today" },
      { dateStr: formatDate(today.clone().add(1, "day")), displayName: "Tomorrow" },
      { dateStr: formatDate(today.clone().add(2, "days")), displayName: today.clone().add(2, "days").format("dddd") },
      ...(nextMonday.diff(today, "days") > 2 ? [{ dateStr: formatDate(nextMonday), displayName: "Next week" }] : []),
      { dateStr: formatDate(today.clone().add(14, "days")), displayName: "In two weeks" },
      ...(firstOfNextMonth.diff(today, "days") > 14
        ? [{ dateStr: formatDate(firstOfNextMonth), displayName: "Next month" }]
        : []),
      {
        dateStr: formatDate(inTwoMonths),
        displayName: inTwoMonths.format(`MMMM D${inTwoMonths.year() !== today.year() ? ", YYYY" : ""}`),
      },
    ];

    const filteredDates = defaultDates.filter((date) => {
      const dateMoment = moment(date.dateStr);
      return (
        (!minDate || !dateMoment.clone().endOf("day").isBefore(moment(minDate))) &&
        (!maxDate || !dateMoment.clone().startOf("day").isAfter(moment(maxDate)))
      );
    });

    const defaultEntities = filteredDates.map((e) => ({
      value: e.dateStr,
      label: e.displayName,
      aliases: [],
      icon: markRaw(CalendarIcon),
      entity: e,
    }));

    if (query && query.trim() !== "") {
      const parsedDate = parseDate(query);
      if (parsedDate) {
        const isWithinConstraints =
          (!minDate || !moment(parsedDate).endOf("day").isBefore(moment(minDate))) &&
          (!maxDate || !moment(parsedDate).startOf("day").isAfter(moment(maxDate)));

        if (!isWithinConstraints) {
          return defaultEntities;
        }
        const dateStr = formatDate(parsedDate);
        const displayName = getRelativeTimeForDatesDate(dateStr);
        const parsedEntity = {
          value: dateStr,
          label: displayName,
          aliases: [query],
          icon: markRaw(CalendarIcon),
          entity: { dateStr, displayName },
        };
        return [parsedEntity, ...defaultEntities];
      }
    }

    return defaultEntities;
  },
});

const tagEntityList = (dataStore: DataStore): CommandEntityList<Option> => ({
  name: "tag",
  getEntitiesFn: async () => {
    const currentTags = await actions.visualization.getCurrentTags();
    const currentTagDuids = new Set(currentTags.map((e) => e.duid));

    const allTags = dataStore.getOptionList(dataStore.defaultTagsProperty);
    return allTags.map((e) => ({
      value: e.duid,
      label: e.title,
      aliases: [],
      icon: markRaw(TagsFieldIcon),
      iconArgs: { style: { color: e.colorHex } },
      entity: e,
      selected: currentTagDuids.has(e.duid),
    }));
  },
});

const priorityEntityList = (): CommandEntityList<{ key: Priority | null; label: string }> => ({
  name: "priority",
  getEntitiesFn: async () =>
    actions.visualization.getPossiblePriorities().map((e) => ({
      value: e.key,
      label: e.label,
      aliases: [],
      icon: markRaw(PriorityIcon),
      iconArgs: { style: { color: e.key ? PRIORITY_COLOR_MAP.get(e.key) : undefined } },
      entity: { key: e.key, label: e.label },
    })),
});

const sizeEntityList = (dataStore: DataStore): CommandEntityList<{ key: TshirtSize | null; label: string }> => {
  const { format } = dataStore.defaultSizeProperty.adtl;
  const colorMap = SIZE_FORMAT_TO_COLOR_MAP.get(format);
  return {
    name: "size",
    getEntitiesFn: async () =>
      actions.visualization.getPossibleSizes().map((e) => {
        const numberSize = TSHIRT_SIZE_TO_NUMBER_SIZE.get(e.key) ?? undefined;
        return {
          value: e.key ?? UNSIZED_SIZE_LABEL,
          label: e.label,
          aliases: [],
          icon: markRaw(SizeIcon),
          iconArgs: { size: numberSize, format, style: { color: numberSize ? colorMap?.get(numberSize) : undefined } },
          entity: e,
        };
      }),
  };
};

const makeAvailabilityRule = (valueFn: () => boolean, reason: string): CommandAvailabilityRule => ({
  value: computed(valueFn),
  reason,
});

const makeExternalLink = (params: {
  id: CommandId;
  title: string;
  url: string;
  icon: Component;
  aliases?: string[];
}): Command => ({
  id: params.id,
  title: params.title,
  action: {
    type: CommandActionType.EXTERNAL_LINK,
    url: params.url,
  },
  icon: params.icon,
  aliases: params.aliases ?? [],
});

const makeToggleSettingsCallback = (dataStore: DataStore, settingsPage: string) => () => {
  const appStore = dataStore.$useAppStore();
  const router = appStore.$router;
  const routerUtils = appStore.$routerUtils;
  const query = { ...router.currentRoute.value.query };
  const newPage = query.settings !== settingsPage ? settingsPage : undefined;
  router.push(routerUtils.makeLinkToSettingsRef(newPage).value);
};

const makeToggleThemeCallback = (dataStore: DataStore) => () => {
  const userStore = dataStore.$useUserStore();
  const oldThemeDark =
    userStore.theme === Theme.SYSTEM_DEFAULT
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : userStore.theme === Theme.DARK;
  userStore.setTheme(oldThemeDark ? Theme.LIGHT : Theme.DARK);
};

export const getCommandsList = (dataStore: DataStore): CommandSection[] => {
  const appStore = dataStore.$useAppStore();
  const environmentStore = dataStore.$useEnvironmentStore();
  const pageStore = dataStore.$usePageStore();
  const tenantStore = dataStore.$useTenantStore();
  const userStore = dataStore.$useUserStore();

  const makeEditorCommand = (id: CommandId, title: string, keys: Key[], icon: Component): Command => ({
    id,
    title,
    icon,
    shortcut: { displayOverride: keys },
    action: {
      type: CommandActionType.CALLBACK,
      callback: () => runEditorCommand(keys, pageStore.isMac),
    },
    filters: [computed(() => !appStore.commandCenterOpen || canRunEditorCommand(id))],
  });

  // Filters
  const hasDraftTask = computed(() => !!dataStore.taskDraft);
  const hasNoDraftTask = computed(() => !dataStore.taskDraft);
  const isNotOnSpaceOrFolder = computed(() => !appStore.spaceOrFolder);
  const isOnline = computed(() => pageStore.isOnline);
  const isOnSpaceOrFolder = computed(() => !!appStore.spaceOrFolder);
  const taskDetailOpen = computed(() => appStore.taskDetailOpen);
  const taskDetailClosed = computed(() => !appStore.taskDetailOpen);
  const tenantIsDart = computed(() => tenantStore.isDart);
  const isLocalStandard = computed(() => environmentStore.isLocalStandard);
  const tenantHasDueDateFilter = computed(() => tenantStore.dueDateEnabled);
  const tenantHasMultipleAssigneesDisabled = computed(() => !tenantStore.multipleAssigneesEnabled);
  const tenantHasMultipleAssigneesEnabled = computed(() => tenantStore.multipleAssigneesEnabled);
  const tenantHasPriorityFilter = computed(() => tenantStore.priorityEnabled);
  const tenantHasSizeFilter = computed(() => tenantStore.sizeEnabled);
  const tenantHasStartDateFilter = computed(() => tenantStore.startDateEnabled);
  const themeIsNotDark = computed(() => userStore.theme !== Theme.DARK);
  const themeIsNotLight = computed(() => userStore.theme !== Theme.LIGHT);
  const switchViewModeFromCommand = (mode: ViewMode) =>
    switchViewMode(mode, { appStore, router: appStore.$router, userStore });
  const availableViewModeTabs = computed(() => getAvailableViewModeTabs(pageStore.showAlpha));

  // Availability rules
  const layoutSubtasksNotFlat = makeAvailabilityRule(
    () => appStore.subtaskDisplayMode !== SubtaskDisplayMode.FLAT,
    "Subtasks are flat"
  );
  const makeCanEditTask = (e: string) =>
    makeAvailabilityRule(
      () =>
        !!appStore.currentPage?.pageKind &&
        ([PageKind.DARTBOARD, PageKind.VIEW].includes(appStore.currentPage?.pageKind) || appStore.tcmOpen),
      `You're not on a page that ${e}`
    );
  const hasSelectedTasks = makeAvailabilityRule(
    () =>
      appStore.tcmOpen ||
      appStore.selectedTaskDuids.size !== 0 ||
      appStore.hoverRow !== null ||
      appStore.getActiveVisualization().getSelectedRows().length !== 0,
    "You don't have any tasks selected"
  );
  const isAdmin = makeAvailabilityRule(() => userStore.isRoleGte(UserRole.ADMIN), "You are not an admin");
  const isNotGuest = makeAvailabilityRule(() => userStore.isRoleGte(UserRole.MEMBER), "You are a guest");
  const settingsModalNotOpen = makeAvailabilityRule(() => !appStore.settingsModalOpen, "Settings are open");
  const hasAiChatAccess = makeAvailabilityRule(
    () => tenantStore.isSubscriptionGte(Subscription.APP_SUMO) && userStore.hasAccessToAiChat(),
    "Upgrade to use AI chat"
  );

  // Availability rule presets
  const ADMIN_ONLY_RULES = [isAdmin];
  const ASSIGNEE_RULES = [makeCanEditTask("has task assignees"), settingsModalNotOpen, hasSelectedTasks];
  const REVIEWER_RULES = [makeCanEditTask("has task reviewers"), settingsModalNotOpen, hasSelectedTasks];
  const FILTER_RULES = [makeCanEditTask("has filters"), settingsModalNotOpen];
  const LAYOUT_RULES = [makeCanEditTask("has different layouts"), settingsModalNotOpen];
  const NAVIGATION_RULES = [makeCanEditTask("has navigation"), settingsModalNotOpen];
  const NOT_GUEST_RULES = [isNotGuest];
  const PRIORITY_RULES = [makeCanEditTask("allows priorities"), settingsModalNotOpen, hasSelectedTasks];
  const STATUS_CHANGE_RULES = [makeCanEditTask("has task statuses"), settingsModalNotOpen, hasSelectedTasks];
  const SUBTASK_VISIBILITY_RULES = [makeCanEditTask("allows subtasks"), settingsModalNotOpen, layoutSubtasksNotFlat];
  const TASK_EDIT_RULES = [makeCanEditTask("has tasks"), settingsModalNotOpen, hasSelectedTasks];
  const TASK_MOVEMENT_RULES = [makeCanEditTask("allows task movement"), settingsModalNotOpen, hasSelectedTasks];
  const TASK_SELECTION_RULES = [makeCanEditTask("allows selection"), settingsModalNotOpen];

  const commandSections: CommandSection[] = [
    {
      title: "AI",
      commands: [
        {
          id: CommandId.RECOMMEND_PROPERTIES,
          title: "Use AI to fill out properties",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.recommendProperties() },
          availabilityRules: [
            isNotGuest,
            makeCanEditTask("allows property recommendations"),
            settingsModalNotOpen,
            hasSelectedTasks,
          ],
          icon: PropertiesIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Alt, Key.P]) },
          aliases: ["ai", "fields", "recommend", "property", "generate"],
          filters: [isOnline],
        },
        {
          id: CommandId.RECOMMEND_SUBTASKS,
          title: "Use AI to break into subtasks",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.recommendSubtasks() },
          availabilityRules: [
            isNotGuest,
            makeCanEditTask("allows subtask recommendations"),
            settingsModalNotOpen,
            hasSelectedTasks,
          ],
          icon: RecommendSubtasksIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Alt, Key.S]) },
          aliases: ["ai", "split", "decompose", "generate", "up", "down"],
          filters: [isOnline],
        },
        {
          id: CommandId.CHAT_WITH_AI,
          title: "New chat",
          action: {
            type: CommandActionType.CALLBACK,
            callback: async (query) => {
              userStore.setAiChatPanelOpen(true);
              await nextTick();

              const trimmedQuery = query.trim();
              const matchesQuery = trimmedQuery
                ? fuzzySearch(
                    trimmedQuery,
                    [
                      {
                        title: "New chat",
                        aliases: ["assistant", "help", "dart ai", "ai chat", "chat with ai", "start"],
                      },
                    ],
                    ["title", (command) => command.aliases.join()]
                  ).results.length > 0
                : false;

              if (!matchesQuery) {
                appStore.chatPanel?.createNewChatWithText(trimmedQuery);
                return;
              }

              appStore.chatPanel?.createNewChat();
            },
          },
          availabilityRules: [hasAiChatAccess],
          icon: ChatIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Alt, Key.C]) },
          aliases: ["assistant", "help", "dart ai", "ai chat", "chat with ai", "start"],
          persistent: true,
        },
        {
          id: CommandId.TOGGLE_AI_CHAT_PANEL_TO_OPEN,
          title: "Open AI chat panel",
          action: {
            type: CommandActionType.CALLBACK,
            callback: async () => {
              userStore.setAiChatPanelOpen(true);
            },
          },
          availabilityRules: [hasAiChatAccess],
          icon: ChatIcon,
          filters: [computed(() => !userStore.getAiChatPanelOpen())],
          shortcut: { default: makeShortcut([Key.Control, Key.Slash]) },
          aliases: ["assistant", "sidebar", "dart ai"],
        },
        {
          id: CommandId.TOGGLE_AI_CHAT_PANEL_TO_CLOSED,
          title: "Close AI chat panel",
          action: {
            type: CommandActionType.CALLBACK,
            callback: async () => {
              userStore.setAiChatPanelOpen(false);
            },
          },
          availabilityRules: [hasAiChatAccess],
          icon: ChatIcon,
          filters: [computed(() => userStore.getAiChatPanelOpen())],
          shortcut: { default: makeShortcut([Key.Control, Key.Slash]) },
          aliases: ["assistant", "sidebar", "dart ai"],
        },
      ],
    },
    {
      title: "Dartboard",
      commands: [
        {
          id: CommandId.CREATE_TASK,
          title: "Create a task",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.contextualCreate() },
          icon: TaskCreateIcon,
          shortcut: { default: makeShortcut([Key.C]) },
          aliases: ["new", "make", "add"],
          filters: [isNotOnSpaceOrFolder, hasNoDraftTask],
        },
        {
          id: CommandId.RESUME_DRAFT,
          title: "Resume work on the task draft",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.contextualCreate() },
          icon: TaskCreateIcon,
          shortcut: { default: makeShortcut([Key.C]) },
          filters: [isNotOnSpaceOrFolder, hasDraftTask],
        },
        {
          id: CommandId.CREATE_TASK_QUICKLY,
          title: "Quickly create a task",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.createTaskQuickly() },
          availabilityRules: [makeCanEditTask("allows task creation"), settingsModalNotOpen],
          icon: TaskCreateIcon,
          shortcut: { default: makeShortcut([Key.Shift, Key.C]) },
          aliases: ["new", "make", "add", "fast", "nlp", "title"],
        },
        {
          id: CommandId.CREATE_TASK_UNDERNEATH,
          title: "Create task underneath",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.createTaskUnderneath() },
          availabilityRules: TASK_EDIT_RULES,
          icon: TaskCreateIcon,
          shortcut: { default: makeShortcut([Key.Enter]) },
          aliases: ["new", "make", "add", "fast", "title"],
        },
        {
          id: CommandId.EDIT_TITLE,
          title: "Start editing task title",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.startEditingTitle() },
          availabilityRules: TASK_EDIT_RULES,
          icon: EditIcon,
          shortcut: { default: [makeShortcut([Key.E]), makeShortcut([Key.Backspace])] },
          aliases: ["change", "rename"],
        },
        {
          id: CommandId.COPY_LINK,
          title: "Copy link",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.copyTaskLinks() },
          availabilityRules: TASK_EDIT_RULES,
          icon: LinkIcon,
          shortcut: { default: makeShortcut([Key.U]) },
          aliases: ["url"],
        },
        {
          id: CommandId.COPY_BRANCH,
          title: "Copy branch name",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.copyTaskBranchNames() },
          availabilityRules: TASK_EDIT_RULES,
          icon: GitBranchIcon,
          shortcut: { default: makeShortcut([Key.B]) },
          aliases: ["github", "git"],
        },
        {
          id: CommandId.COPY_TASK_AS_PROMPT,
          title: "Copy task as prompt",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.copyTaskAsPrompt() },
          availabilityRules: TASK_EDIT_RULES,
          icon: PromptIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Alt, Key.M]) },
          aliases: ["prompt", "markdown", "ai", "llm", "agent"],
        },
        {
          id: CommandId.CHANGE_STATUS,
          title: "Change status",
          action: {
            type: CommandActionType.SELECT,
            entities: statusEntityList(),
            placeholder: "Select status",
            callback: (entity) => actions.visualization.setStatus(entity.duid),
          } satisfies CommandActionSelect<Status>,
          availabilityRules: STATUS_CHANGE_RULES,
          icon: StatusFieldIcon,
          shortcut: { default: makeShortcut([Key.S]) },
          aliases: ["update", "set", "mark"],
        },
        {
          id: CommandId.CHANGE_STATUS_TO_DEFAULT_FINISHED,
          title: "Change status to finished",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.setStatusDone() },
          availabilityRules: STATUS_CHANGE_RULES,
          icon: FinishedIcon,
          shortcut: { default: makeShortcut([Key.F]) },
          aliases: ["update", "set", "mark", "completed", "done"],
        },
        {
          id: CommandId.CYCLE_UNSTARTED_STATUSES,
          title: "Cycle through unstarted statuses",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.cycleStatuses(StatusKind.UNSTARTED),
          },
          availabilityRules: STATUS_CHANGE_RULES,
          icon: UnstartedIcon,
          shortcut: { default: makeShortcut([Key.Digit1]) },
          aliases: ["update", "set", "mark", "to-do", "todo"],
        },
        {
          id: CommandId.CYCLE_STARTED_STATUSES,
          title: "Cycle through started statuses",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.cycleStatuses(StatusKind.STARTED),
          },
          availabilityRules: STATUS_CHANGE_RULES,
          icon: StartedIcon,
          shortcut: { default: makeShortcut([Key.Digit2]) },
          aliases: ["update", "set", "mark", "doing"],
        },
        {
          id: CommandId.CYCLE_BLOCKED_STATUSES,
          title: "Cycle through blocked statuses",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.cycleStatuses(StatusKind.BLOCKED),
          },
          availabilityRules: STATUS_CHANGE_RULES,
          icon: BlockedIcon,
          shortcut: { default: makeShortcut([Key.Digit3]) },
          aliases: ["update", "set", "mark"],
        },
        {
          id: CommandId.CYCLE_FINISHED_STATUSES,
          title: "Cycle through finished statuses",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.cycleStatuses(StatusKind.FINISHED),
          },
          availabilityRules: STATUS_CHANGE_RULES,
          icon: FinishedIcon,
          shortcut: { default: makeShortcut([Key.Digit4]) },
          aliases: ["update", "set", "mark"],
        },
        {
          id: CommandId.CYCLE_CANCELED_STATUSES,
          title: "Cycle through canceled statuses",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.cycleStatuses(StatusKind.CANCELED),
          },
          availabilityRules: STATUS_CHANGE_RULES,
          icon: CanceledIcon,
          shortcut: { default: makeShortcut([Key.Digit5]) },
          aliases: ["update", "set", "mark"],
        },
        {
          id: CommandId.CHANGE_ASSIGNEE_TO_SELF,
          title: "Set assigned to me",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.setAssignee(userStore.duid),
          },
          availabilityRules: ASSIGNEE_RULES,
          icon: AssigneeFieldIcon,
          shortcut: { default: makeShortcut([Key.Digit0]) },
          aliases: ["assignee", "change", "update", "set", "mark", "add", "reassign", "myself"],
        },
        {
          id: CommandId.CHANGE_ASSIGNEE_TO_UNASSIGNED,
          title: "Set unassigned",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.setAssignee(null) },
          availabilityRules: ASSIGNEE_RULES,
          icon: UnassignedIcon,
          shortcut: { default: makeShortcut([Key.Digit9]) },
          aliases: ["assignee", "change", "update", "set", "mark", "remove"],
        },
        { id: CommandId.SWAP_ASSIGNEE, title: "Swap assignees" },
        {
          id: CommandId.CHANGE_ASSIGNEE,
          title: "Change assignee",
          action: {
            type: CommandActionType.SELECT,
            entities: possibleAssigneeEntityList(dataStore),
            placeholder: "New assignee",
            callback: (entity) => actions.visualization.setAssignee(entity.key),
          } satisfies CommandActionSelect<{ key: string | null; label: string }>,
          availabilityRules: ASSIGNEE_RULES,
          icon: AssigneeFieldIcon,
          shortcut: { default: makeShortcut([Key.A]) },
          aliases: ["person", "user", "member", "reassign", "teammate", "update", "set", "mark"],
          filters: [tenantHasMultipleAssigneesDisabled],
        },
        {
          id: CommandId.EDIT_ASSIGNEES,
          title: "Edit assignees",
          action: {
            type: CommandActionType.MULTISELECT,
            entities: assigneesEntityList(dataStore),
            placeholder: "Select assignees",
            callback: (entities) => actions.visualization.setAssignees(entities.map((e) => e.key).filter(isDefined)),
          } satisfies CommandActionMultiSelect<{ key: string | null; label: string }>,
          availabilityRules: ASSIGNEE_RULES,
          icon: MultipleUsersFieldIcon,
          shortcut: { default: makeShortcut([Key.A]) },
          aliases: [
            "person",
            "dri",
            "user",
            "member",
            "reassign",
            "teammate",
            "add",
            "remove",
            "update",
            "set",
            "mark",
          ],
          filters: [tenantHasMultipleAssigneesEnabled],
        },
        {
          id: CommandId.CHANGE_REVIEWER_TO_SELF,
          title: "Set reviewer to me",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.setReviewer(userStore.duid),
          },
          availabilityRules: REVIEWER_RULES,
          icon: ReviewerFieldIcon,
          aliases: ["reviewer", "change", "update", "set", "mark", "add", "myself"],
        },
        {
          id: CommandId.CHANGE_REVIEWER,
          title: "Change reviewer",
          action: {
            type: CommandActionType.SELECT,
            entities: possibleReviewerEntityList(dataStore),
            placeholder: "New reviewer",
            callback: (entity) => actions.visualization.setReviewer(entity.key),
          } satisfies CommandActionSelect<{ key: string | null; label: string }>,
          availabilityRules: REVIEWER_RULES,
          icon: ReviewerFieldIcon,
          aliases: ["person", "user", "member", "reassign", "teammate", "update", "set", "mark"],
          filters: [computed(() => !tenantStore.multipleReviewersEnabled)],
        },
        {
          id: CommandId.EDIT_REVIEWERS,
          title: "Edit reviewers",
          action: {
            type: CommandActionType.MULTISELECT,
            entities: reviewersEntityList(dataStore),
            placeholder: "Select reviewers",
            callback: (entities) => actions.visualization.setReviewers(entities.map((e) => e.key).filter(isDefined)),
          } satisfies CommandActionMultiSelect<{ key: string | null; label: string }>,
          availabilityRules: REVIEWER_RULES,
          icon: MultipleUsersFieldIcon,
          aliases: ["person", "user", "member", "reassign", "teammate", "add", "remove", "update", "set", "mark"],
          filters: [computed(() => tenantStore.multipleReviewersEnabled)],
        },
        {
          id: CommandId.CHANGE_PRIORITY_TO_UNPRIORITIZED,
          title: "Set as unprioritized",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.setPriority(null) },
          availabilityRules: PRIORITY_RULES,
          icon: PriorityFieldIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.Backquote]) },
          aliases: ["change", "update", "mark"],
          filters: [tenantHasPriorityFilter],
        },
        {
          id: CommandId.CHANGE_PRIORITY_TO_CRITICAL,
          title: "Set priority to critical",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.setPriority(Priority.CRITICAL),
          },
          availabilityRules: PRIORITY_RULES,
          icon: PriorityFieldIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.Digit1]) },
          aliases: ["change", "update", "mark", "p0"],
          filters: [tenantHasPriorityFilter],
        },
        {
          id: CommandId.CHANGE_PRIORITY_TO_HIGH,
          title: "Set priority to high",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.setPriority(Priority.HIGH),
          },
          availabilityRules: PRIORITY_RULES,
          icon: PriorityFieldIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.Digit2]) },
          aliases: ["change", "update", "mark", "p1"],
          filters: [tenantHasPriorityFilter],
        },
        {
          id: CommandId.CHANGE_PRIORITY_TO_MEDIUM,
          title: "Set priority to medium",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.setPriority(Priority.MEDIUM),
          },
          availabilityRules: PRIORITY_RULES,
          icon: PriorityFieldIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.Digit3]) },
          aliases: ["change", "update", "mark", "p2"],
          filters: [tenantHasPriorityFilter],
        },
        {
          id: CommandId.CHANGE_PRIORITY_TO_LOW,
          title: "Set priority to low",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.setPriority(Priority.LOW) },
          availabilityRules: PRIORITY_RULES,
          icon: PriorityFieldIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.Digit4]) },
          aliases: ["change", "update", "mark", "p3"],
          filters: [tenantHasPriorityFilter],
        },
        {
          id: CommandId.CHANGE_PRIORITY,
          title: "Change priority",
          action: {
            type: CommandActionType.SELECT,
            entities: priorityEntityList(),
            placeholder: "Select priority",
            callback: (entity) => actions.visualization.setPriority(entity.key),
          } satisfies CommandActionSelect<{ key: Priority | null; label: string }>,
          availabilityRules: PRIORITY_RULES,
          icon: PriorityFieldIcon,
          shortcut: { default: makeShortcut([Key.P]) },
          aliases: ["importance", "urgency", "update", "set", "mark", "p0", "p1", "p2", "p3"],
          filters: [tenantHasPriorityFilter],
        },
        {
          id: CommandId.CHANGE_SIZE,
          title: "Change size",
          action: {
            type: CommandActionType.SELECT,
            entities: sizeEntityList(dataStore),
            placeholder: "Select size",
            callback: (entity) => actions.visualization.setSize(entity.key),
          } satisfies CommandActionSelect<{ key: TshirtSize | null; label: string }>,
          availabilityRules: [makeCanEditTask("allows sizes"), settingsModalNotOpen, hasSelectedTasks],
          icon: SizeFieldIcon,
          shortcut: { default: makeShortcut([Key.I]) },
          aliases: ["estimate", "story points", "update", "set", "mark"],
          filters: [tenantHasSizeFilter],
        },
        {
          id: CommandId.EDIT_TAGS,
          title: "Change tags",
          action: {
            type: CommandActionType.MULTISELECT,
            entities: tagEntityList(dataStore),
            placeholder: "Select tags",
            callback: (entities) => actions.visualization.setTags(entities.map((e) => e.duid)),
            create: {
              label: "Create tag",
              callback: (value) => dataStore.createOption(value, dataStore.defaultTagsProperty),
            },
          } satisfies CommandActionMultiSelect<Option>,
          availabilityRules: [makeCanEditTask("has task tags"), settingsModalNotOpen, hasSelectedTasks],
          icon: TagsFieldIcon,
          shortcut: { default: makeShortcut([Key.T]) },
          aliases: ["labels", "components", "change", "add", "remove", "update", "set", "mark"],
        },
        {
          id: CommandId.CHANGE_START_DATE,
          title: "Change start date",
          action: {
            type: CommandActionType.SELECT,
            entities: dateEntityList(async () => {
              const constraints = await getSelectedTasksDateConstraints();
              return { minDate: undefined, maxDate: constraints.maxDate };
            }),
            placeholder: "Select start date",
            callback: (entity) => actions.visualization.setStartAt(moment(entity.dateStr).toDate()),
          } satisfies CommandActionSelect<{ dateStr: string; displayName: string }>,
          availabilityRules: [makeCanEditTask("allows start dates"), settingsModalNotOpen, hasSelectedTasks],
          icon: StartDateFieldIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.R]) },
          aliases: ["do", "update", "set", "mark"],
          filters: [tenantHasStartDateFilter],
        },
        {
          id: CommandId.CHANGE_DUE_DATE,
          title: "Change due date",
          action: {
            type: CommandActionType.SELECT,
            entities: dateEntityList(async () => {
              const constraints = await getSelectedTasksDateConstraints();
              return { minDate: constraints.minDate, maxDate: undefined };
            }),
            placeholder: "Select due date",
            callback: (entity) => actions.visualization.setDueAt(moment(entity.dateStr).toDate()),
          } satisfies CommandActionSelect<{ dateStr: string; displayName: string }>,
          availabilityRules: [makeCanEditTask("allows due dates"), settingsModalNotOpen, hasSelectedTasks],
          icon: DueDateFieldIcon,
          shortcut: { default: makeShortcut([Key.R]) },
          aliases: ["end", "do", "update", "set", "mark"],
          filters: [tenantHasDueDateFilter],
        },
        {
          id: CommandId.SET_REMINDER,
          title: "Change reminder",
          action: {
            type: CommandActionType.SELECT,
            entities: dateEntityList(),
            placeholder: "Select reminder date",
            callback: (entity) => actions.visualization.setRemindAt(moment(entity.dateStr).toDate()),
          } satisfies CommandActionSelect<{ dateStr: string; displayName: string }>,
          availabilityRules: [makeCanEditTask("allows snoozing"), settingsModalNotOpen, hasSelectedTasks],
          icon: ReminderIcon,
          aliases: ["alert", "notification", "snooze"],
        },
        {
          id: CommandId.INDENT_TO_SUBTASK,
          title: "Indent to subtask",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.subtaskIndent() },
          availabilityRules: TASK_EDIT_RULES,
          icon: IndentIcon,
          shortcut: { default: makeShortcut([Key.Tab]) },
          aliases: ["parent", "convert"],
        },
        {
          id: CommandId.OUTDENT_TO_UNSUBTASK,
          title: "Outdent to unsubtask",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.subtaskOutdent() },
          availabilityRules: TASK_EDIT_RULES,
          icon: OutdentIcon,
          shortcut: { default: makeShortcut([Key.Shift, Key.Tab]) },
          aliases: ["parent", "un-subtask", "convert"],
        },
        { id: CommandId.CHANGE_PARENT, title: "Change parent" },
        {
          id: CommandId.MOVE_TASK_DOWN,
          title: "Move task down",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.moveTaskDown() },
          availabilityRules: TASK_MOVEMENT_RULES,
          icon: ArrowDownIcon,
          shortcut: {
            default: [makeShortcut([Key.Control, Key.Alt, Key.ArrowDown]), makeShortcut([Key.Control, Key.Alt, Key.J])],
          },
          aliases: ["rearrange", "reorder", "shift"],
        },
        {
          id: CommandId.MOVE_TASK_UP,
          title: "Move task up",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.moveTaskUp() },
          availabilityRules: TASK_MOVEMENT_RULES,
          icon: ArrowUpIcon,
          shortcut: {
            default: [makeShortcut([Key.Control, Key.Alt, Key.ArrowUp]), makeShortcut([Key.Control, Key.Alt, Key.K])],
          },
          aliases: ["rearrange", "reorder", "shift"],
        },
        {
          id: CommandId.CREATE_SUBTASKS,
          title: "Add subtask",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.createSubtask() },
          availabilityRules: [makeCanEditTask("allows creating subtasks"), settingsModalNotOpen, hasSelectedTasks],
          icon: SubtasksIndentedIcon,
          shortcut: { default: makeShortcut([Key.L]) },
          aliases: ["children", "break down", "split", "create"],
        },
        {
          id: CommandId.ADD_ATTACHMENT,
          title: "Add attachment",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.addAttachments() },
          availabilityRules: TASK_EDIT_RULES,
          icon: AttachmentFieldIcon,
          shortcut: { default: makeShortcut([Key.N]) },
          aliases: ["file", "document", "upload"],
        },
        {
          id: CommandId.ADD_LINK,
          title: "Add link",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.addLink() },
          availabilityRules: TASK_EDIT_RULES,
          icon: LinkFieldIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.L]) },
          aliases: ["url", "website"],
        },
        {
          id: CommandId.REPLICATE_TASK,
          title: "Replicate task",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.replicateTasks() },
          availabilityRules: TASK_EDIT_RULES,
          icon: ReplicateIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.D]) },
          aliases: ["duplicate", "copy", "clone"],
        },
        {
          id: CommandId.DELETE_TASK,
          title: "Delete task",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.deleteTask() },
          availabilityRules: [makeCanEditTask("allows deletion"), settingsModalNotOpen, hasSelectedTasks],
          icon: TrashIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Backspace]) },
          aliases: ["remove", "archive"],
        },
        {
          id: CommandId.MOVE_TO_DARTBOARD,
          title: "Move task to any dartboard",
          action: {
            type: CommandActionType.SELECT,
            entities: dartboardEntityList(dataStore),
            placeholder: "Select dartboard",
            callback: (e) => actions.visualization.moveTasksToDartboard(e.duid),
          } satisfies CommandActionSelect<Dartboard>,
          availabilityRules: [makeCanEditTask("allows moving"), settingsModalNotOpen, hasSelectedTasks],
          icon: DartboardIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.D]) },
          aliases: ["send", "transfer", "sprint"],
        },
        {
          id: CommandId.UNDO,
          title: "Undo",
          action: { type: CommandActionType.CALLBACK, callback: () => dataStore.undo() },
          icon: UndoIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Z]) },
        },
        {
          id: CommandId.REDO,
          title: "Redo",
          action: { type: CommandActionType.CALLBACK, callback: () => dataStore.redo() },
          icon: RedoIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.Z]) },
        },
        {
          id: CommandId.SET_LAYOUT_TO_NEXT,
          title: "Switch to next layout",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => {
              const nextLayout = LAYOUT_KIND_TO_NEXT_LAYOUT_KIND.get(appStore.layoutKind);
              if (nextLayout) {
                appStore.setLayoutKind(nextLayout);
              }
            },
          },
          availabilityRules: LAYOUT_RULES,
          icon: LayoutIcon,
          shortcut: { default: makeShortcut([Key.V], [Key.N]) },
          aliases: ["toggle", "view", "visualization", "next", "forward", "subtasks", "indented", "collapsed", "flat"],
        },
        {
          id: CommandId.SET_LAYOUT_TO_PREVIOUS,
          title: "Switch to previous layout",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => {
              const previousLayout = LAYOUT_KIND_TO_PREVIOUS_LAYOUT_KIND.get(appStore.layoutKind);
              if (previousLayout) {
                appStore.setLayoutKind(previousLayout);
              }
            },
          },
          availabilityRules: LAYOUT_RULES,
          icon: LayoutIcon,
          shortcut: { default: makeShortcut([Key.V], [Key.P]) },
          aliases: ["toggle", "view", "visualization", "previous", "back", "subtasks", "indented", "collapsed", "flat"],
        },
        {
          id: CommandId.SET_LAYOUT_TO_LIST,
          title: "Switch to list layout",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.setLayoutKind(LayoutKind.LIST) },
          availabilityRules: LAYOUT_RULES,
          icon: ListViewIcon,
          shortcut: { default: makeShortcut([Key.V], [Key.L]) },
          aliases: ["view", "visualization", "list", "spreadsheet"],
        },
        {
          id: CommandId.SET_LAYOUT_TO_BOARD,
          title: "Switch to board layout",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.setLayoutKind(LayoutKind.BOARD) },
          availabilityRules: LAYOUT_RULES,
          icon: BoardViewIcon,
          shortcut: { default: makeShortcut([Key.V], [Key.B]) },
          aliases: ["view", "visualization", "board", "kanban"],
        },
        {
          id: CommandId.SET_LAYOUT_TO_CALENDAR,
          title: "Switch to calendar layout",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.setLayoutKind(LayoutKind.CALENDAR) },
          availabilityRules: LAYOUT_RULES,
          icon: CalendarViewIcon,
          shortcut: { default: makeShortcut([Key.V], [Key.C]) },
          aliases: ["view", "visualization", "calendar", "timeline"],
        },
        {
          id: CommandId.SET_LAYOUT_TO_ROADMAP,
          title: "Switch to roadmap layout",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.setLayoutKind(LayoutKind.ROADMAP) },
          availabilityRules: LAYOUT_RULES,
          icon: RoadmapViewIcon,
          shortcut: { default: makeShortcut([Key.V], [Key.R]) },
          aliases: ["view", "visualization", "roadmap", "timeline", "gantt", "flowchart"],
        },
        {
          id: CommandId.SET_LAYOUT_TO_WORKFLOW,
          title: "Switch to workflow layout",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.setLayoutKind(LayoutKind.WORKFLOW) },
          availabilityRules: LAYOUT_RULES,
          icon: WorkflowViewIcon,
          shortcut: { default: makeShortcut([Key.V], [Key.W]) },
          aliases: [
            "view",
            "visualization",
            "workflow",
            "canvas",
            "mind map",
            "whiteboard",
            "freeform",
            "process",
            "graph",
            "orchestration",
            "pipeline",
            "automation",
          ],
        },
        {
          id: CommandId.TOGGLE_FILTERS_AND_SORTS_TO_SHOWN,
          title: "Show filters and sorts",
          action: { type: CommandActionType.CALLBACK, callback: () => userStore.setShowFilters(true) },
          availabilityRules: FILTER_RULES,
          icon: FilterIcon,
          shortcut: { default: makeShortcut([Key.Y]) },
          aliases: ["toggle", "search", "find", "organize"],
          filters: [computed(() => !userStore.getShowFilters())],
        },
        {
          id: CommandId.TOGGLE_FILTERS_AND_SORTS_TO_HIDDEN,
          title: "Hide filters and sorts",
          action: { type: CommandActionType.CALLBACK, callback: () => userStore.setShowFilters(false) },
          availabilityRules: FILTER_RULES,
          icon: FilterIcon,
          shortcut: { default: makeShortcut([Key.Y]) },
          aliases: ["toggle", "search", "find", "organize"],
          filters: [computed(() => userStore.getShowFilters())],
        },
        { id: CommandId.ADD_FILTER, title: "Add filter" },
        { id: CommandId.REMOVE_FILTER, title: "Remove filter" },
        { id: CommandId.CLEAR_FILTER, title: "Clear filter" },
        { id: CommandId.CHANGE_FILTER_APPLICABILITY, title: "Change how filter applies" },
        { id: CommandId.CHANGE_FILTER_VALUES, title: "Change filter values" },
        {
          id: CommandId.CHANGE_FILTER_CONNECTOR,
          title: "Change how filter values are related",
        },
        { id: CommandId.SAVE_TASK, title: "Save task" },
        {
          id: CommandId.SAVE_TASK_OPEN_IN_CREATION_MODAL,
          title: "Save task",
          hideInShortcutsModal: true,
          shortcut: { default: makeShortcut([Key.Control, Key.Enter]) },
        },
        {
          id: CommandId.SAVE_TASK_OPEN_IN_CREATION_MODAL_AND_CREATE_ANOTHER,
          title: "Save task and create more",
          hideInShortcutsModal: true,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.Enter]) },
        },
        {
          id: CommandId.SAVE_CHAT,
          title: "Send chat",
          hideInShortcutsModal: true,
          shortcut: { default: makeShortcut([Key.Control, Key.Enter]) },
        },
        { id: CommandId.STOP_CHAT, title: "Stop responding" },
        {
          id: CommandId.SAVE_COMMENT,
          title: "Send comment",
          hideInShortcutsModal: true,
          shortcut: { default: makeShortcut([Key.Control, Key.Enter]) },
        },
        {
          id: CommandId.ZOOM_OUT,
          title: "Zoom out",
          hideInShortcutsModal: true,
          shortcut: { displayOverride: [Key.Control, "Scroll out"] },
        },
        {
          id: CommandId.ZOOM_IN,
          title: "Zoom in",
          hideInShortcutsModal: true,
          shortcut: { displayOverride: [Key.Control, "Scroll in"] },
        },
        { id: CommandId.SWAP, title: "Swap" },
        { id: CommandId.START_TIMER, title: "Start timer" },
        { id: CommandId.START_NEXT_SPRINT, title: "Start next sprint" },
      ],
    },
    {
      title: "Docs",
      commands: [
        {
          id: CommandId.CREATE_DOC,
          title: "Create a doc",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.contextualCreate() },
          icon: DocIcon,
          shortcut: { default: makeShortcut([Key.C]) },
          aliases: ["new", "make", "add", "document"],
          filters: [isOnSpaceOrFolder],
        },
      ],
    },
    {
      title: "Navigation",
      commands: [
        {
          id: CommandId.TOGGLE_LEFTBAR_TO_EXPANDED,
          title: "Expand sidebar",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => {
              if (appStore.leftbarInCollapsibleMode) {
                appStore.setMobileLeftbarOpen(true);
              } else {
                userStore.setLeftbarExpanded(true);
              }
            },
          },
          icon: LayoutLeftIcon,
          filters: [
            computed(() =>
              appStore.leftbarInCollapsibleMode ? !appStore.mobileLeftbarOpen : !userStore.getLeftbarExpanded()
            ),
          ],
          shortcut: { default: makeShortcut([Key.Control, Key.Backslash]) },
          aliases: ["toggle", "show", "open", "leftbar", "navigation", "panel"],
        },
        {
          id: CommandId.TOGGLE_LEFTBAR_TO_COLLAPSED,
          title: "Collapse sidebar",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => {
              if (appStore.leftbarInCollapsibleMode) {
                appStore.setMobileLeftbarOpen(false);
              } else {
                userStore.setLeftbarExpanded(false);
              }
            },
          },
          icon: LayoutLeftIcon,
          filters: [
            computed(() =>
              appStore.leftbarInCollapsibleMode ? appStore.mobileLeftbarOpen : userStore.getLeftbarExpanded()
            ),
          ],
          shortcut: { default: makeShortcut([Key.Control, Key.Backslash]) },
          aliases: ["toggle", "hide", "close", "leftbar", "navigation", "panel"],
        },
        ...VIEW_MODE_TAB_COMMAND_IDS.map((commandId, index): Command => {
          const shortcutKey = VIEW_MODE_DIGIT_KEYS[index];
          const tab = availableViewModeTabs.value[index];
          return {
            id: commandId,
            title: tab ? `Switch to ${tab.title.toLowerCase()} mode` : `Switch to tab ${index + 1}`,
            action: {
              type: CommandActionType.CALLBACK,
              callback: () => {
                const currentTab = availableViewModeTabs.value[index];
                if (currentTab) {
                  switchViewModeFromCommand(currentTab.value);
                }
              },
            },
            icon: tab?.icon ?? ProjectIcon,
            shortcut: shortcutKey ? { default: makeShortcut([Key.G], [shortcutKey]) } : undefined,
            aliases: [...(tab?.aliases ?? []), "leftbar", "sidebar"],
            filters: [
              computed(() => availableViewModeTabs.value.length > 1 && index < availableViewModeTabs.value.length),
            ],
          };
        }),
        {
          id: CommandId.TOGGLE_TASK_OPEN_IN_DETAIL,
          title: "Open task",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.selectAndToggleTaskDetail(),
          },
          availabilityRules: [makeCanEditTask("allows opening tasks"), settingsModalNotOpen],
          icon: RightbarIcon,
          shortcut: { default: [makeShortcut([Key.O]), makeShortcut([Key.Space])] },
          aliases: ["open", "view", "rightbar"],
          filters: [taskDetailClosed],
        },
        {
          id: CommandId.TOGGLE_TASK_OPEN_IN_DETAIL,
          title: "Close task",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => actions.visualization.selectAndToggleTaskDetail(),
          },
          availabilityRules: [makeCanEditTask("allows closing tasks"), settingsModalNotOpen],
          icon: RightbarIcon,
          shortcut: { default: [makeShortcut([Key.O]), makeShortcut([Key.Space])] },
          aliases: ["close", "view", "rightbar"],
          filters: [taskDetailOpen],
        },
        {
          id: CommandId.OPEN_TASK,
          title: "Open task",
          hideInShortcutsModal: true,
          shortcut: { default: makeShortcut([Key.Space]) },
        },
        {
          id: CommandId.EDIT_DESCRIPTION,
          title: "Open description",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.jumpToDescription() },
          availabilityRules: [makeCanEditTask("allows editing descriptions"), settingsModalNotOpen, hasSelectedTasks],
          icon: DescriptionFieldIcon,
          shortcut: { default: makeShortcut([Key.Semicolon]) },
          aliases: ["details", "body", "notes", "write description", "write a description"],
        },
        {
          id: CommandId.GO_UP,
          title: "Go up",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.moveSelectionUp() },
          availabilityRules: NAVIGATION_RULES,
          icon: ChevronUpIcon,
          shortcut: { default: [makeShortcut([Key.ArrowUp]), makeShortcut([Key.K])] },
          aliases: ["previous", "back"],
        },
        {
          id: CommandId.GO_DOWN,
          title: "Go down",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.moveSelectionDown() },
          availabilityRules: NAVIGATION_RULES,
          icon: ChevronDownIcon,
          shortcut: { default: [makeShortcut([Key.ArrowDown]), makeShortcut([Key.J])] },
          aliases: ["next", "forward"],
        },
        {
          id: CommandId.GO_TO_TOP_OF_DARTBOARD,
          title: "Go to top",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.moveSelectionToTop() },
          availabilityRules: NAVIGATION_RULES,
          icon: ChevronUpDoubleIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.ArrowUp]) },
          aliases: ["top", "beginning", "start", "first"],
        },
        {
          id: CommandId.GO_TO_BOTTOM_OF_DARTBOARD,
          title: "Go to bottom",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.moveSelectionToBottom() },
          availabilityRules: NAVIGATION_RULES,
          icon: ChevronDownDoubleIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.ArrowDown]) },
          aliases: ["bottom", "end", "last"],
        },
        {
          id: CommandId.SELECT_TASK,
          title: "Select task",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.selectCurrent() },
          availabilityRules: TASK_SELECTION_RULES,
          icon: CheckboxFieldIcon,
          filters: [computed(() => actions.visualization.getHoverRow()?.isSelected() === false)],
          shortcut: { default: makeShortcut([Key.X]) },
          aliases: ["mark", "check"],
        },
        {
          id: CommandId.DESELECT_TASK,
          title: "Deselect task",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.selectCurrent() },
          availabilityRules: TASK_SELECTION_RULES,
          icon: CheckboxFieldIcon,
          filters: [computed(() => actions.visualization.getHoverRow()?.isSelected() === true)],
          shortcut: { default: makeShortcut([Key.X]) },
          aliases: ["unmark", "uncheck"],
        },
        {
          id: CommandId.SELECT_ALL,
          title: "Select all",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.getActiveVisualization().selectAll() },
          availabilityRules: TASK_SELECTION_RULES,
          icon: SelectAllIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.A]) },
          aliases: ["mark", "check"],
        },
        {
          id: CommandId.SELECT_NEXT_BELOW,
          title: "Select next below",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.selectRangeDown() },
          availabilityRules: TASK_SELECTION_RULES,
          icon: ArrowDownIcon,
          shortcut: { default: makeShortcut([Key.Shift, Key.ArrowDown]) },
        },
        {
          id: CommandId.SELECT_NEXT_ABOVE,
          title: "Select next above",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.selectRangeUp() },
          availabilityRules: TASK_SELECTION_RULES,
          icon: ArrowUpIcon,
          shortcut: { default: makeShortcut([Key.Shift, Key.ArrowUp]) },
        },
        {
          id: CommandId.SELECT_ALL_BELOW,
          title: "Select all below",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.selectAllBelow() },
          availabilityRules: TASK_SELECTION_RULES,
          icon: SelectAllIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.ArrowDown]) },
          aliases: ["mark", "check"],
        },
        {
          id: CommandId.SELECT_ALL_ABOVE,
          title: "Select all above",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.selectAllAbove() },
          availabilityRules: TASK_SELECTION_RULES,
          icon: SelectAllIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.ArrowUp]) },
          aliases: ["mark", "check"],
        },
        {
          id: CommandId.HIDE_SUBTASKS,
          title: "Hide subtasks",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.moveSelectionLeft() },
          availabilityRules: SUBTASK_VISIBILITY_RULES,
          icon: SubtasksCollapsedIcon,
          shortcut: { default: makeShortcut([Key.ArrowLeft]) },
          aliases: ["indented", "collapsed", "flat"],
        },
        {
          id: CommandId.SHOW_SUBTASKS,
          title: "Show subtasks",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.visualization.moveSelectionRight() },
          availabilityRules: SUBTASK_VISIBILITY_RULES,
          icon: SubtasksIndentedIcon,
          shortcut: { default: makeShortcut([Key.ArrowRight]) },
          aliases: ["outdent", "uncollapse", "expand"],
        },
        {
          id: CommandId.TOGGLE_SEARCH,
          title: "Search tasks",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => appStore.setSearchModalOpen(!appStore.searchModalOpen),
          },
          icon: SearchIcon,
          shortcut: { default: makeShortcut([Key.Slash]) },
          aliases: ["find", "filter", "query"],
        },
        {
          id: CommandId.GO_TO_HOME,
          title: "Go to Home",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.$router.push("/home") },
          icon: HomeIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.H]) },
        },
        {
          id: CommandId.GO_TO_MY_TASKS,
          title: "Go to My tasks",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.$router.push("/my-tasks") },
          icon: MyTasksIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.M]) },
        },
        {
          id: CommandId.GO_TO_INBOX,
          title: "Go to Inbox",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.$router.push("/inbox") },
          icon: InboxIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.I]) },
        },
        {
          id: CommandId.GO_TO_AGENTS,
          title: "Go to Agents",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.$router.push("/agents") },
          icon: AgentIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.A]) },
          aliases: ["ai", "assistant", "automation", "bot"],
          availabilityRules: NOT_GUEST_RULES,
        },
        {
          id: CommandId.GO_TO_DASHBOARDS,
          title: "Go to Dashboards",
          aliases: ["analytics", "chart", "statistics", "stats", "intelligence", "progress", "report"],
        },
        {
          id: CommandId.GO_TO_VIEWS,
          title: "Go to Views",
          aliases: ["layouts", "visualizations"],
        },
        {
          id: CommandId.GO_TO_TRASH,
          title: "Go to Trash",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.$router.push("/trash") },
          icon: TrashIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.R]) },
        },
        {
          id: CommandId.GO_TO_VIEW,
          title: "Go to view",
          action: {
            type: CommandActionType.SELECT,
            entities: viewEntityList(dataStore),
            placeholder: "Select view",
            callback: (entity) => {
              appStore.$router.push(getViewLink(entity));
            },
          } satisfies CommandActionSelect<View>,
          icon: ViewIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.V]) },
        },
        {
          id: CommandId.GO_TO_DASHBOARD,
          title: "Go to dashboard",
          action: {
            type: CommandActionType.SELECT,
            entities: dashboardEntityList(dataStore),
            placeholder: "Select dashboard",
            callback: (entity) => {
              appStore.$router.push(getDashboardLink(entity));
            },
          } satisfies CommandActionSelect<Dashboard>,
          icon: DashboardIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.B]) },
          aliases: ["analytics", "chart", "statistics", "stats", "intelligence", "progress", "report"],
        },
        {
          id: CommandId.GO_TO_DARTBOARD,
          title: "Go to dartboard",
          action: {
            type: CommandActionType.SELECT,
            entities: dartboardEntityList(dataStore),
            placeholder: "Select dartboard",
            callback: (entity) => {
              appStore.$router.push(getDartboardLink(entity, dataStore.getSpaceByDuid));
            },
          } satisfies CommandActionSelect<Dartboard>,
          icon: DartboardIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.D]) },
        },
        {
          id: CommandId.GO_TO_TASK,
          title: "Go to task",
          action: {
            type: CommandActionType.SELECT,
            entities: taskEntityList(dataStore),
            placeholder: "Select task",
            callback: (entity) => {
              appStore.$router.push(getTaskLink(entity));
            },
          },
          icon: TaskIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.T]) },
        },
        {
          id: CommandId.GO_TO_FOLDER,
          title: "Go to folder",
          action: {
            type: CommandActionType.SELECT,
            entities: folderEntityList(dataStore),
            placeholder: "Select folder",
            callback: (entity) => {
              appStore.$router.push(getFolderLink(entity));
            },
          } satisfies CommandActionSelect<Folder>,
          icon: FolderIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.F]) },
        },
        {
          id: CommandId.GO_TO_DOC,
          title: "Go to doc",
          action: {
            type: CommandActionType.SELECT,
            entities: docEntityList(dataStore),
            placeholder: "Select doc",
            callback: (entity) => {
              appStore.$router.push(getDocLink(entity));
            },
          } satisfies CommandActionSelect<Doc>,
          icon: DocIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.O]) },
        },
        {
          id: CommandId.GO_TO_CHAT,
          title: "Go to chat",
          action: {
            type: CommandActionType.SELECT,
            entities: chatEntityList(dataStore),
            placeholder: "Select chat",
            callback: (entity) => {
              appStore.$router.push(getChatLink(entity));
            },
          } satisfies CommandActionSelect<Chat>,
          icon: ChatIcon,
        },
        {
          id: CommandId.NAV_REFRESH,
          title: "Refresh",
          action: { type: CommandActionType.CALLBACK, callback: () => window.location.reload() },
          icon: RefreshIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.R]) },
          aliases: ["reload", "restart", "reset"],
        },
        {
          id: CommandId.RESET_VIEW,
          title: "Reset view",
          hideInShortcutsModal: true,
          shortcut: { displayOverride: [Key.Escape] },
          aliases: ["close", "exit", "quit", "escape"],
        },
        {
          id: CommandId.RESET_VIEW_CLOSE,
          title: "Close",
          hideInShortcutsModal: true,
          shortcut: { displayOverride: [Key.Escape] },
        },
        {
          id: CommandId.GO_TO_MEDIA_PREVIOUS,
          title: "Previous",
          hideInShortcutsModal: true,
          shortcut: { displayOverride: [Key.ArrowLeft] },
        },
        {
          id: CommandId.GO_TO_MEDIA_NEXT,
          title: "Next",
          hideInShortcutsModal: true,
          shortcut: { displayOverride: [Key.ArrowRight] },
        },
        { id: CommandId.NAV_BACK, title: "Back" },
        { id: CommandId.NAV_FORWARD, title: "Forward" },
        { id: CommandId.VIEW_COMMENTS, title: "View comments" },
        { id: CommandId.VIEW_ATTACHMENTS, title: "View attachments" },
        { id: CommandId.VIEW_LINKS, title: "View links" },
        { id: CommandId.CREATE_DARTBOARD, title: "Create a dartboard" },
        { id: CommandId.CREATE_DASHBOARD, title: "Create a dashboard" },
        { id: CommandId.CREATE_FOLDER, title: "Create a folder" },
        { id: CommandId.CREATE_SPACE, title: "Create a space" },
        { id: CommandId.CREATE_VIEW, title: "Create a view" },
      ],
    },
    {
      title: "Configuration",
      commands: [
        {
          id: CommandId.OPEN_SETTINGS,
          title: "Open settings",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "workspace") },
          icon: SettingsIcon,
          shortcut: { default: makeShortcut([Key.G], [Key.S]) },
          aliases: [
            "preferences",
            "configuration",
            "go to",
            "configure",
            "set",
            "setup",
            "workflow",
            "import",
            "name",
            "company",
            "team",
            "image",
            "avatar",
            "logo",
            "picture",
            "backlog",
            "multiple",
            "assignees",
            "due",
            "dates",
            "reminders",
            "enable",
            "disable",
            "people",
            "size",
            "sizing",
            "estimates",
            "priorities",
            "priority",
            "account",
            "invite",
            "add",
            "teammates",
            "remove",
            "kick",
            "leave",
            "membership",
            "access",
            "level",
            "directory",
            "admin",
            "statuses",
            "blocked",
            "canceled",
            "won't do",
            "more",
            "awaiting deployment",
            "duplicate",
            "finished",
            "unstarted",
            "started",
            "in progress",
            "doing",
            "done",
            "todo",
            "rename",
            "reorder",
            "color",
            "icon",
            "plan",
            "billing",
            "invoice",
            "receipt",
            "money",
            "pay",
            "upgrade",
            "sales",
            "downgrade",
            "charges",
            "credit cards",
            "features",
            "comparison",
            "files",
            "export",
            "manage",
            "data",
            "analysis",
            "download",
            "emails",
            "full names",
            "first names",
            "last names",
            "initials",
            "theme",
            "user",
            "username",
            "change",
            "password",
            "system",
            "light",
            "dark",
            "mode",
            "integrate",
            "github",
            "git",
            "gitlab",
            "bitbucket",
            "integration",
            "notify",
            "notifications",
            "email",
            "slack",
            "updates",
            "messages",
          ],
        },
        {
          id: CommandId.TOGGLE_THEME,
          title: "Switch between light and dark modes",
          hideInCommandCenter: true,
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => {
              const oldThemeDark =
                userStore.theme === Theme.SYSTEM_DEFAULT
                  ? window.matchMedia("(prefers-color-scheme: dark)").matches
                  : userStore.theme === Theme.DARK;
              userStore.setTheme(oldThemeDark ? Theme.LIGHT : Theme.DARK);
            },
          },
          shortcut: { default: makeShortcut([Key.Control, Key.M]) },
          aliases: ["dark mode", "light mode", "appearance"],
        },
        {
          id: CommandId.SWITCH_TO_LIGHT_MODE,
          title: "Switch to light mode",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleThemeCallback(dataStore) },
          icon: ThemeIcon,
          shortcut: { displayOverride: [Key.Control, Key.M] },
          aliases: ["dark mode", "toggle theme", "appearance", "light mode"],
          filters: [themeIsNotLight],
        },
        {
          id: CommandId.SWITCH_TO_DARK_MODE,
          title: "Switch to dark mode",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleThemeCallback(dataStore) },
          icon: ThemeIcon,
          shortcut: { displayOverride: [Key.Control, Key.M] },
          aliases: ["dark mode", "toggle theme", "appearance", "light mode"],
          filters: [themeIsNotDark],
        },
        {
          id: CommandId.MANAGE_ACCOUNT,
          title: "Manage account",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "account") },
          icon: AccountSettingsIcon,
          aliases: [
            "configure",
            "set",
            "setup",
            "configuration",
            "preferences",
            "name",
            "image",
            "logo",
            "avatar",
            "email",
            "full name",
            "first name",
            "last name",
            "initials",
            "theme",
            "user",
            "username",
            "picture",
            "mode",
            "change",
            "dark",
            "light",
            "system",
            "password",
            "profile",
          ],
        },
        {
          id: CommandId.MANAGE_NOTIFICATIONS,
          title: "Manage notifications",
          action: {
            type: CommandActionType.CALLBACK,
            callback: makeToggleSettingsCallback(dataStore, "notifications"),
          },
          icon: NotificationIcon,
          aliases: [
            "configure",
            "set",
            "setup",
            "configuration",
            "preferences",
            "email",
            "manage",
            "account",
            "slack",
            "notify",
            "notifications",
            "updates",
            "custom",
            "recommended",
            "messages",
          ],
        },
        {
          id: CommandId.MANAGE_WORKSPACE_PREFERENCES,
          title: "Manage workspace preferences",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "workspace") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: WorkspaceSettingsIcon,
          aliases: [
            "configure",
            "setup",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "name",
            "company",
            "team",
            "workspace",
            "logo",
            "image",
            "avatar",
            "picture",
            "account",
          ],
        },
        {
          id: CommandId.INVITE_TEAMMATES,
          title: "Invite teammates",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "teammates") },
          availabilityRules: NOT_GUEST_RULES,
          icon: UsersInviteIcon,
          aliases: [
            "invite",
            "add",
            "people",
            "teammates",
            "email",
            "settings",
            "configure",
            "set",
            "configuration",
            "admin",
            "preferences",
            "change",
            "manage",
            "membership",
            "access",
            "status",
            "remove",
            "leave",
            "kick",
            "workspace",
            "team",
            "directory",
          ],
        },
        {
          id: CommandId.MANAGE_IDENTITY_AND_PROVISIONING,
          title: "Manage identity and provisioning",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "identity") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: IdentityIcon,
          aliases: [
            "setup",
            "configure",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "create",
            "sso",
            "saml",
            "oauth 2.0",
            "scim",
            "automatic",
            "automation",
            "automate",
            "users",
            "groups",
          ],
        },
        {
          id: CommandId.MANAGE_PROPERTIES,
          title: "Manage properties",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "properties") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: PropertiesIcon,
          aliases: [
            "setup",
            "configure",
            "statuses",
            "blocked",
            "canceled",
            "won't do",
            "more",
            "add",
            "duplicate",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "awaiting deployment",
            "finished",
            "in progress",
            "rename",
            "doing",
            "done",
            "todo",
            "started",
            "unstarted",
            "icon",
            "color",
            "reorder",
            "multiple assignees",
            "dates",
            "date range",
            "start date",
            "tags",
            "fields",
            "status",
            "user",
            "assignees",
            "users",
            "assignee",
            "labels",
            "workflows",
            "properties",
            "property",
            "field",
            "value",
            "values",
            "columns",
          ],
        },
        {
          id: CommandId.MANAGE_FORMS,
          title: "Manage forms",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "forms") },
          availabilityRules: NOT_GUEST_RULES,
          icon: FormIcon,
          aliases: [
            "setup",
            "configure",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "create",
          ],
        },
        {
          id: CommandId.MANAGE_PLANS_AND_BILLING,
          title: "Manage plans and billing",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "billing") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: PlansAndBillingSettingsIcon,
          aliases: [
            "configure",
            "plan",
            "billing",
            "receipt",
            "money",
            "pay",
            "upgrade",
            "settings",
            "set",
            "setup",
            "configuration",
            "preferences",
            "change",
            "manage",
            "invoice",
            "downgrade",
            "sales",
            "cancel",
            "charge",
            "credit card",
            "features",
            "comparison",
            "account",
          ],
        },
        {
          id: CommandId.IMPORT_DATA,
          title: "Import data",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "import") },
          availabilityRules: NOT_GUEST_RULES,
          icon: ImportIcon,
          aliases: [
            "export",
            "linear",
            "asana",
            "integration",
            "jira",
            "airtable",
            "clickup",
            "basecamp",
            "github",
            "issues",
            "upload",
            "motion",
            "notion",
            "monday.com",
            "height",
            "smartsheet",
            "todoist",
            "trello",
            "settings",
            "configure",
            "set",
            "setup",
            "configuration",
            "preferences",
            "change",
            "files",
            "manage",
            "migrate",
            "upgrade",
            "data",
          ],
        },
        {
          id: CommandId.EXPORT_DATA,
          title: "Export data",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "export") },
          icon: ExportIcon,
          aliases: [
            "download",
            "settings",
            "analysis",
            "data",
            "configure",
            "set",
            "setup",
            "configuration",
            "preferences",
            "change",
            "manage",
          ],
        },
        {
          id: CommandId.MANAGE_CHATGPT_INTEGRATION,
          title: "Manage ChatGPT integration",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "chatgpt") },
          availabilityRules: NOT_GUEST_RULES,
          icon: ChatgptIcon,
          aliases: [
            "setup",
            "configure",
            "add",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "integrate",
            "chatgpt",
            "openai",
            "llm",
            "chat",
            "chatbot",
            "large language model",
            "generative ai",
            "artificial intelligence",
            "gai",
          ],
        },
        {
          id: CommandId.MANAGE_CLAUDE_INTEGRATION,
          title: "Manage Claude integration",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "claude") },
          icon: ClaudeIcon,
          aliases: [
            "setup",
            "configure",
            "add",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "integrate",
            "llm",
            "chat",
            "chatbot",
            "large language model",
            "generative ai",
            "artificial intelligence",
            "general ai",
            "gai",
            "claude",
            "anthropic",
            "mcp",
            "model context protocol",
          ],
        },
        {
          id: CommandId.MANAGE_API,
          title: "Manage API",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "cli") },
          icon: ApiIcon,
          aliases: [
            "configure",
            "set",
            "setup",
            "configuration",
            "preferences",
            "initials",
            "manage",
            "change",
            "cli",
            "api",
            "code",
            "application programming interface",
            "command line interface",
            "tools",
            "python",
            "programming",
          ],
        },
        {
          id: CommandId.MANAGE_WEBHOOKS,
          title: "Manage webhooks",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "webhooks") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: WebhooksIcon,
          aliases: [
            "configure",
            "set",
            "setup",
            "configuration",
            "preferences",
            "initials",
            "manage",
            "change",
            "api",
            "code",
            "application programming interface",
            "tools",
            "programming",
            "webhooks",
            "updates",
            "automated",
          ],
        },
        {
          id: CommandId.MANAGE_AUTOMATIONS,
          title: "Manage automations",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "automations") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: AutomationsIcon,
          aliases: [
            "setup",
            "configure",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "create",
            "automate",
            "AI",
            "artificial intelligence",
            "butler",
            "shortcuts",
          ],
        },
        {
          id: CommandId.MANAGE_EMAIL_INTEGRATION,
          title: "Manage email integration",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "mail") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: MailIcon,
          aliases: [
            "configure",
            "set",
            "setup",
            "configuration",
            "preferences",
            "initials",
            "manage",
            "change",
            "tools",
            "mail",
            "send",
            "message",
            "email",
            "forward",
            "gmail",
            "superhuman",
            "outlook",
            "apple mail",
            "proton mail",
            "em client",
            "thunderbird",
            "mailbird",
            "airmail",
            "yahoo mail",
          ],
        },
        {
          id: CommandId.MANAGE_SLACK_INTEGRATION,
          title: "Manage Slack integration",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "slack") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: SlackIcon,
          aliases: [
            "setup",
            "configure",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "integrate",
            "slack",
            "chat",
            "messages",
            "notifications",
            "messaging",
            "DMs",
            "channels",
            "create",
            "comment",
          ],
        },
        {
          id: CommandId.MANAGE_DISCORD_INTEGRATION,
          title: "Manage Discord integration",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "discord") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: DiscordIcon,
          aliases: [
            "setup",
            "configure",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "integrate",
            "chat",
            "messages",
            "notifications",
            "messaging",
            "DMs",
            "channels",
            "create",
            "comment",
            "discord",
          ],
        },
        {
          id: CommandId.MANAGE_GITHUB_INTEGRATION,
          title: "Manage GitHub integration",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "github") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: GithubIcon,
          aliases: [
            "setup",
            "configure",
            "add",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "integrate",
            "github",
            "git",
            "gitlab",
            "bitbucket",
          ],
        },
        {
          id: CommandId.MANAGE_ZAPIER_INTEGRATION,
          title: "Manage Zapier integration",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "zapier") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: ZapierIcon,
          aliases: [
            "setup",
            "configure",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "create",
            "automate",
            "integrate",
            "integrations",
            "automations",
            "workflow",
          ],
        },
        {
          id: CommandId.CONFIGURE_ZOHO_FLOW_INTEGRATION,
          title: "Configure Zoho Flow integration",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "zoho-flow") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: ZohoFlowIcon,
          aliases: [
            "setup",
            "configure",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "create",
            "automate",
            "integrate",
            "integrations",
            "automations",
            "workflow",
          ],
        },
        {
          id: CommandId.MANAGE_PIPEDREAM_INTEGRATION,
          title: "Manage Pipedream integration",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "pipedream") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: PipedreamIcon,
          aliases: [
            "setup",
            "configure",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "create",
            "automate",
            "integrate",
            "integrations",
            "automations",
            "workflow",
          ],
        },
        {
          id: CommandId.MANAGE_NOTION_INTEGRATION,
          title: "Manage Notion integration",
          action: { type: CommandActionType.CALLBACK, callback: makeToggleSettingsCallback(dataStore, "notion") },
          availabilityRules: ADMIN_ONLY_RULES,
          icon: NotionIcon,
          aliases: [
            "setup",
            "configure",
            "add",
            "settings",
            "set",
            "configuration",
            "preferences",
            "change",
            "manage",
            "integrate",
            "notion",
            "document",
            "description",
            "link",
            "unlink",
            "documentation",
            "editor",
          ],
        },
      ],
    },
    {
      title: "Help and resources",
      commands: [
        makeExternalLink({
          id: CommandId.HELP_CENTER,
          title: "Help center",
          url: "https://help.dartai.com",
          icon: HelpCenterIcon,
          aliases: [
            "help",
            "document",
            "documentation",
            "read more",
            "how",
            "can",
            "why",
            "where",
            "when",
            "what",
            "docs",
            "specs",
            "specifications",
          ],
        }),
        {
          id: CommandId.TOGGLE_KEYBOARD_SHORTCUTS,
          title: "Keyboard shortcuts",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => appStore.setShortcutsModalOpen(!appStore.shortcutsModalOpen),
          },
          icon: KeyboardShortcutsIcon,
          shortcut: {
            default: [makeShortcut([Key.Shift, Key.Slash]), makeShortcut([Key.QuestionMark])],
            displayOverride: [Key.QuestionMark],
          },
          aliases: ["shortcuts", "help", "keys", "binds"],
        },
        {
          id: CommandId.OPEN_IN_BROWSER,
          title: "Open in browser",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => window.open(appStore.$router.currentRoute.value.fullPath, "_blank"),
          },
          icon: WebsiteIcon,
          aliases: ["browser", "web", "app", "chrome", "edge", "brave", "arc", "internet", "safari", "firefox"],
          filters: [computed(() => !pageStore.isNativeApp)],
        },
        {
          id: CommandId.FIND_IN_PAGE,
          title: "Find in page",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => DESKTOP.openSearch(pageStore.theme === "dark"),
          },
          icon: SearchIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.F]) },
          aliases: ["search"],
          filters: [computed(() => pageStore.isDesktopApp && !pageStore.isPublic)],
        },
        {
          id: CommandId.FIND_IN_PAGE,
          title: "Find in page",
          shortcut: { displayOverride: [Key.Control, Key.F] },
          aliases: ["search"],
          filters: [computed(() => !pageStore.isDesktopApp && !pageStore.isPublic)],
        },
        makeExternalLink({
          id: CommandId.USE_THE_POMODORO_TIMER,
          title: "Use the pomodoro timer",
          url: "https://dartai.com/tools/pomodoro-timer?nr=1",
          icon: PomodoroIcon,
          aliases: ["pomodoro", "timer", "clock", "method"],
        }),
        makeExternalLink({
          id: CommandId.CAREERS_AT_DART,
          title: "Careers at Dart",
          url: "https://dartai.com/careers?nr=1",
          icon: CareersIcon,
          aliases: ["jobs", "work", "hiring", "opportunities", "opportunity", "engineering", "build"],
        }),
        {
          id: CommandId.SHARE_FEEDBACK,
          title: "Share feedback",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.setFeedbackModalOpen(true) },
          icon: ChatIcon,
          aliases: ["bug", "report", "issue", "suggestion"],
        },
        makeExternalLink({
          id: CommandId.EMAIL_SUPPORT,
          title: "Email support",
          url: "mailto:support@dartai.com?subject=Dart%20Support",
          icon: MailIcon,
          aliases: ["help", "support", "email", "bug", "feature", "request"],
        }),
        {
          id: CommandId.OPEN_IN_DESKTOP_APP,
          title: "Open in desktop app",
          action: { type: CommandActionType.CALLBACK, callback: () => actions.app.openInDesktopApp() },
          icon: DesktopOrWebAppIcon,
          aliases: ["native", "desktop", "mac", "windows", "macos", "app", "download"],
          filters: [computed(() => !pageStore.isDesktopApp)],
        },
        {
          id: CommandId.GET_THE_MOBILE_APP,
          title: "Get the mobile app",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => {
              if (pageStore.isMobile) {
                window.open("/redirect/mobile-app", "_blank");
              } else {
                appStore.setAppDownloadModalOpen(true);
              }
            },
          },
          icon: MobileAppIcon,
          aliases: ["mobile", "iphone", "android", "phone", "app", "ios", "ipad"],
          filters: [computed(() => !pageStore.isMobileApp)],
        },
        makeExternalLink({
          id: CommandId.USE_DART_IN_CHATGPT,
          title: "Use Dart in ChatGPT",
          url: "https://chat.openai.com/g/g-eKfjMUXwr-dart",
          icon: ChatgptIcon,
          aliases: ["artificial intelligence", "chat", "dart", "chatgpt", "gpt", "ai", "openai"],
        }),
        makeExternalLink({
          id: CommandId.API_DOCS,
          title: "API docs",
          url: "https://app.dartai.com/api/v0/public/docs/",
          icon: ApiIcon,
          aliases: ["api", "schema", "docs", "documentation", "public"],
        }),
        makeExternalLink({
          id: CommandId.USE_THE_TYPESCRIPT_AND_JAVASCRIPT_LIBRARY,
          title: "Use the TypeScript and JavaScript library",
          url: "https://npmjs.com/package/dart-tools-ts",
          icon: ApiIcon,
          aliases: [
            "terminal",
            "console",
            "API",
            "code",
            "coding",
            "CLI",
            "library",
            "typescript",
            "javascript",
            "node",
          ],
        }),
        makeExternalLink({
          id: CommandId.USE_THE_PYTHON_LIBRARY,
          title: "Use the Python library",
          url: "https://pypi.org/project/dart-tools/",
          icon: ApiIcon,
          aliases: ["terminal", "console", "API", "python", "code", "coding", "CLI", "library"],
        }),
        makeExternalLink({
          id: CommandId.OUR_WEBSITE,
          title: "Our website",
          url: "https://www.dartai.com?nr=1",
          icon: WebsiteIcon,
          aliases: ["dart", "landing page", "info", "public"],
        }),
        makeExternalLink({
          id: CommandId.STATUS,
          title: "Status",
          url: "https://status.dartai.com",
          icon: StatusWebsiteIcon,
          aliases: ["uptime", "downtime", "nines", "slas"],
        }),
        makeExternalLink({
          id: CommandId.CHANGELOG,
          title: "Changelog",
          url: "https://changelog.dartai.com",
          icon: ChangelogIcon,
          aliases: ["update", "release", "notes"],
        }),
        makeExternalLink({
          id: CommandId.FIND_US_ON_LINKEDIN,
          title: "Find us on LinkedIn",
          url: "https://www.linkedin.com/company/dart-ai",
          icon: LinkedinIcon,
          aliases: ["follow", "support", "business"],
        }),
        makeExternalLink({
          id: CommandId.FIND_US_ON_INSTAGRAM,
          title: "Find us on Instagram",
          url: "https://instagram.com/dartappofficial",
          icon: InstagramIcon,
          aliases: ["follow", "support", "watch", "videos", "connect"],
        }),
        makeExternalLink({
          id: CommandId.FIND_US_ON_YOUTUBE,
          title: "Find us on YouTube",
          url: "https://www.youtube.com/@dart-ai",
          icon: YoutubeIcon,
          aliases: ["follow", "support", "watch", "videos"],
        }),
        makeExternalLink({
          id: CommandId.FIND_US_ON_X,
          title: "Find us on X",
          url: "https://x.com/its_dart",
          icon: XCompanyIcon,
          aliases: ["tweet", "twitter", "follow", "support"],
        }),
        makeExternalLink({
          id: CommandId.FIND_US_ON_THREADS,
          title: "Find us on Threads",
          url: "https://www.threads.net/@dartappofficial",
          icon: ThreadsIcon,
          aliases: ["follow", "support", "threads", "thread"],
        }),
        makeExternalLink({
          id: CommandId.FIND_US_ON_BLUESKY,
          title: "Find us on Bluesky",
          url: "https://bsky.app/profile/dart-ai.bsky.social",
          icon: BlueskyIcon,
          aliases: ["follow", "support", "connect"],
        }),
        makeExternalLink({
          id: CommandId.FIND_US_ON_TIKTOK,
          title: "Find us on TikTok",
          url: "https://tiktok.com/@dart_ai",
          icon: TiktokIcon,
          aliases: ["follow", "support", "watch", "videos"],
        }),
        makeExternalLink({
          id: CommandId.PRIVACY_POLICY,
          title: "Privacy policy",
          url: "https://app.dartai.com/legal/privacy-policy",
          icon: PrivacyPolicyIcon,
          aliases: ["personal data", "GDPR", "data protection", "read", "legal", "ccpa"],
        }),
        makeExternalLink({
          id: CommandId.TERMS_OF_USE,
          title: "Terms of use",
          url: "https://app.dartai.com/legal/terms-of-use",
          icon: TermsOfUseIcon,
          aliases: ["legal", "terms and conditions"],
        }),
        {
          id: CommandId.LOG_OUT,
          title: "Log out",
          action: { type: CommandActionType.CALLBACK, callback: () => appStore.$router.push("/logout") },
          icon: LogOutIcon,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.Q]) },
          aliases: ["sign out", "exit"],
        },
        {
          id: CommandId.TOGGLE_COMMAND_CENTER,
          title: "Command center",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => {
              appStore.setCommandCenterOpen(!appStore.commandCenterOpen);
            },
          },
          hideInCommandCenter: true,
          shortcut: { default: makeShortcut([Key.Control, Key.K]) },
        },
        {
          id: CommandId.OPEN_VUE_INSPECTOR,
          title: "Open inspector",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => {
              const inspector = (window as unknown as { __VUE_INSPECTOR__: VueInspectorClient | undefined })
                .__VUE_INSPECTOR__;
              inspector?.enable();
            },
          },
          hideInCommandCenter: true,
          hideInShortcutsModal: true,
          shortcut: { default: makeShortcut([Key.Control, Key.Shift, Key.I]) },
          filters: [tenantIsDart, isLocalStandard],
        },
        {
          id: CommandId.SHOW_STANDUP_TIMER,
          title: "Show standup timer",
          action: {
            type: CommandActionType.CALLBACK,
            callback: () => {
              appStore.showStandupTimer = true;
            },
          },
          icon: PomodoroIcon,
          filters: [tenantIsDart],
        },
      ],
    },
    {
      title: "Quick add",
      commands: [
        {
          id: CommandId.QC_PREFIX_GENERAL,
          title: "Set any property",
          shortcut: { displayOverride: [asMarkdown("/")] },
        },
        {
          id: CommandId.QC_PREFIX_DARTBOARD,
          title: "Choose dartboard",
          shortcut: { displayOverride: [asMarkdown("dartboard")] },
        },
        {
          id: CommandId.QC_PREFIX_STATUS,
          title: "Choose status",
          shortcut: { displayOverride: [asMarkdown("status")] },
        },
        {
          id: CommandId.QC_PREFIX_ASSIGNEE,
          title: "Choose assignee",
          filters: [tenantHasMultipleAssigneesDisabled],
          shortcut: { displayOverride: [asMarkdown("@ "), OR, asMarkdown(" assign")] },
        },
        {
          id: CommandId.QC_PREFIX_ASSIGNEES,
          title: "Choose assignee(s)",
          filters: [tenantHasMultipleAssigneesEnabled],
          shortcut: { displayOverride: [asMarkdown("@ "), OR, asMarkdown(" assign")] },
        },
        {
          id: CommandId.QC_PREFIX_TAG,
          title: "Choose tag(s)",
          shortcut: { displayOverride: [asMarkdown("tag")] },
        },
        {
          id: CommandId.QC_PREFIX_PRIORITY,
          title: "Choose priority",
          filters: [tenantHasPriorityFilter],
          shortcut: { displayOverride: [asMarkdown("priority")] },
        },
        {
          id: CommandId.QC_PREFIX_DUE_DATE,
          title: "Choose due date",
          filters: [tenantHasDueDateFilter],
          shortcut: { displayOverride: [asMarkdown("due")] },
        },
        {
          id: CommandId.QC_PREFIX_SIZE,
          title: "Choose size",
          filters: [tenantHasSizeFilter],
          shortcut: { displayOverride: [asMarkdown("size")] },
        },
        {
          id: CommandId.QC_UNPREFIX_DARTBOARD,
          title: "Set dartboard",
          shortcut: { displayOverride: [asMarkdown("in [active]")] },
        },
        {
          id: CommandId.QC_UNPREFIX_STATUS,
          title: "Set status",
          shortcut: { displayOverride: [asMarkdown("[doing] status")] },
        },
        {
          id: CommandId.QC_UNPREFIX_ASSIGNEE,
          title: "Set assignee",
          shortcut: { displayOverride: [asMarkdown("[email] "), OR, asMarkdown(" [name]")] },
        },
        {
          id: CommandId.QC_UNPREFIX_TAG,
          title: "Set tag",
          shortcut: { displayOverride: [asMarkdown("[bug] tag")] },
        },
        {
          id: CommandId.QC_UNPREFIX_PRIORITY,
          title: "Set priority",
          filters: [tenantHasPriorityFilter],
          shortcut: { displayOverride: [asMarkdown("p[0-3] "), OR, asMarkdown(" [high] priority")] },
        },
        {
          id: CommandId.QC_UNPREFIX_DUE_DATE,
          title: "Set due date",
          filters: [tenantHasDueDateFilter],
          shortcut: { displayOverride: [asMarkdown("[next week] "), OR, asMarkdown(" [7/21]")] },
        },
        {
          id: CommandId.QC_UNPREFIX_SIZE,
          title: "Set size",
          filters: [tenantHasSizeFilter],
          shortcut: { displayOverride: [asMarkdown("[small] size")] },
        },
        {
          id: CommandId.QC_IGNORE_SUGGESTION,
          title: "Ignore suggestion",
          shortcut: { displayOverride: [Key.Backspace] },
        },
      ],
    },
    {
      title: "Text",
      commands: [
        makeEditorCommand(
          CommandId.EDITOR_SET_STANDARD_TEXT,
          "Standard text",
          [Key.Control, Key.Alt, Key.Digit0],
          StandardTextIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_SET_LARGE_HEADER,
          "Large header",
          [Key.Control, Key.Alt, Key.Digit1],
          LargeHeaderIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_SET_MEDIUM_HEADER,
          "Medium header",
          [Key.Control, Key.Alt, Key.Digit2],
          MediumHeaderIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_SET_SMALL_HEADER,
          "Small header",
          [Key.Control, Key.Alt, Key.Digit3],
          SmallHeaderIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_SET_CODE_BLOCK,
          "Code block",
          [Key.Control, Key.Alt, Key.Digit4],
          CodeBlockIcon
        ),
        makeEditorCommand(CommandId.EDITOR_SET_QUOTE, "Quote", [Key.Control, Key.Alt, Key.Digit5], QuoteBlockIcon),
        makeEditorCommand(
          CommandId.EDITOR_SET_TOGGLE_BLOCK,
          "Toggle block",
          [Key.Control, Key.Alt, Key.Digit6],
          ToggleSectionIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_SET_HORIZONTAL_RULE,
          "Horizontal rule",
          [Key.Control, Key.Alt, Key.Digit7],
          HorizontalRuleIcon
        ),
        makeEditorCommand(CommandId.EDITOR_SET_TABLE, "Table", [Key.Control, Key.Alt, Key.Digit8], TableChartIcon),
        makeEditorCommand(CommandId.EDITOR_BOLD, "Bold", [Key.Control, Key.B], BoldIcon),
        makeEditorCommand(CommandId.EDITOR_ITALICIZE, "Italicize", [Key.Control, Key.I], ItalicIcon),
        makeEditorCommand(CommandId.EDITOR_UNDERLINE, "Underline", [Key.Control, Key.U], UnderlineIcon),
        makeEditorCommand(
          CommandId.EDITOR_STRIKETHROUGH,
          "Strikethrough",
          [Key.Control, Key.Shift, Key.S],
          StrikethroughIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_SET_CODE_SNIPPET,
          "Code snippet",
          [Key.Control, Key.Shift, Key.Digit6],
          CodeSnippetIcon
        ),
        makeEditorCommand(CommandId.EDITOR_ADD_LINK, "Link", [Key.Control, Key.L], LinkIcon),
        makeEditorCommand(CommandId.EDITOR_TEXT_OR_BG_COLOR, "Color", [Key.Control, Key.Shift, Key.H], ColorPickerIcon),
        makeEditorCommand(
          CommandId.EDITOR_SET_CHECKLIST,
          "Checklist",
          [Key.Control, Key.Shift, Key.Digit9],
          ChecklistIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_SET_NUMBERED_LIST,
          "Numbered list",
          [Key.Control, Key.Shift, Key.Digit7],
          NumberedListIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_SET_BULLETED_LIST,
          "Bulleted list",
          [Key.Control, Key.Shift, Key.Digit8],
          UnorderedListIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_TOGGLE_CHECKED,
          "Check or uncheck item",
          [Key.Control, Key.Alt, Key.X],
          ChecklistIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_MOVE_BLOCK_UP,
          "Move block up",
          [Key.Control, Key.Alt, Key.ArrowUp],
          ArrowUpIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_MOVE_BLOCK_DOWN,
          "Move block down",
          [Key.Control, Key.Alt, Key.ArrowDown],
          ArrowDownIcon
        ),
        makeEditorCommand(CommandId.EDITOR_USE_AI, "Use AI", [Key.Control, Key.Alt, Key.U], RecommendationIcon),
        makeEditorCommand(
          CommandId.EDITOR_IMPROVE_CONTENT,
          "Improve text",
          [Key.Control, Key.Alt, Key.I],
          RecommendationIcon
        ),
        makeEditorCommand(
          CommandId.EDITOR_START_DICTATION,
          "Start dictation",
          [Key.Control, Key.Alt, Key.A],
          MicrophoneIcon
        ),
        {
          id: CommandId.EDITOR_CANCEL_DICTATION,
          title: "Cancel dictation",
          shortcut: { displayOverride: [Key.Escape] },
        },
        {
          id: CommandId.EDITOR_CONFIRM_DICTATION,
          title: "Insert the dictated text at the cursor",
          shortcut: { displayOverride: [Key.Enter] },
        },
      ],
    },
    {
      title: "Markdown",
      commands: [
        {
          id: CommandId.EDITOR_AI_ACTIONS,
          title: "AI actions",
          filters: [isOnline],
          shortcut: { displayOverride: [Key.Slash] },
        },
        {
          id: CommandId.MARKDOWN_SET_LARGE_HEADER,
          title: "Large header",
          shortcut: { displayOverride: [asMarkdown("# "), asText("text")] },
        },
        {
          id: CommandId.MARKDOWN_SET_MEDIUM_HEADER,
          title: "Medium header",
          shortcut: { displayOverride: [asMarkdown("## "), asText("text")] },
        },
        {
          id: CommandId.MARKDOWN_SET_SMALL_HEADER,
          title: "Small header",
          shortcut: { displayOverride: [asMarkdown("### "), asText("text")] },
        },
        {
          id: CommandId.MARKDOWN_SET_CODE_BLOCK,
          title: "Code block",
          shortcut: { displayOverride: [asMarkdown("```"), asText("code")] },
        },
        {
          id: CommandId.MARKDOWN_SET_QUOTE,
          title: "Quote",
          shortcut: { displayOverride: [asMarkdown("> "), asText("quote")] },
        },
        {
          id: CommandId.MARKDOWN_SET_HORIZONTAL_RULE,
          title: "Horizontal rule",
          shortcut: { displayOverride: [asMarkdown("---")] },
        },
        {
          id: CommandId.MARKDOWN_BOLD,
          title: "Bold",
          shortcut: { displayOverride: [asMarkdown("**"), asText("text"), asMarkdown("**")] },
        },
        {
          id: CommandId.MARKDOWN_ITALICIZE,
          title: "Italicize",
          shortcut: { displayOverride: [asMarkdown("*"), asText("text"), asMarkdown("*")] },
        },
        {
          id: CommandId.MARKDOWN_UNDERLINE,
          title: "Underline",
          shortcut: {
            displayOverride: [asMarkdown("<u>"), asText("text"), asMarkdown("</u>")],
          },
        },
        {
          id: CommandId.MARKDOWN_STRIKETHROUGH,
          title: "Strikethrough",
          shortcut: { displayOverride: [asMarkdown("~~"), asText("text"), asMarkdown("~~")] },
        },
        {
          id: CommandId.MARKDOWN_ADD_IMAGE,
          title: "Add image",
          shortcut: {
            displayOverride: [asMarkdown("!["), asText("alt text"), asMarkdown("]("), asText("url"), asMarkdown(")")],
          },
        },
        {
          id: CommandId.MARKDOWN_SET_CODE_SNIPPET,
          title: "Code snippet",
          shortcut: { displayOverride: [asMarkdown("`"), asText("code"), asMarkdown("`")] },
        },
        {
          id: CommandId.MARKDOWN_ADD_LINK,
          title: "Add link",
          shortcut: {
            displayOverride: [asMarkdown("["), asText("text"), asMarkdown("]("), asText("url"), asMarkdown(")")],
          },
        },
        {
          id: CommandId.MARKDOWN_HIGHLIGHT,
          title: "Highlight",
          hideInShortcutsModal: true,
          shortcut: { displayOverride: [asMarkdown("=="), asText("text"), asMarkdown("==")] },
        },
        {
          id: CommandId.MARKDOWN_SET_CHECKLIST,
          title: "Checklist",
          shortcut: { displayOverride: [asMarkdown("[ ] "), OR, asMarkdown(" [x]")] },
        },
        {
          id: CommandId.MARKDOWN_SET_NUMBERED_LIST,
          title: "Numbered list",
          shortcut: { displayOverride: [asMarkdown("1. ")] },
        },
        {
          id: CommandId.MARKDOWN_SET_BULLETED_LIST,
          title: "Bulleted list",
          shortcut: { displayOverride: [asMarkdown("- ")] },
        },
        {
          id: CommandId.MARKDOWN_SET_TABLE,
          title: "Table",
          shortcut: { displayOverride: [asMarkdown("| "), asText("header"), asMarkdown(" | ")] },
        },
      ],
    },
  ];

  return commandSections.map((section) => ({
    ...section,
    commands: section.commands.map((e): Command => {
      if ("icon" in e && e.icon) {
        return { ...e, icon: markRaw(e.icon) };
      }
      return e;
    }),
  }));
};

export const getEntitiesList = (dataStore: DataStore): CommandEntitySection[] => {
  const appStore = dataStore.$useAppStore();

  return [
    {
      label: "Tasks",
      list: taskEntityList(dataStore),
      callback: (entity) => {
        appStore.$router.push(getTaskLink(entity));
      },
    } satisfies CommandEntitySection<Task>,
    {
      label: "Docs",
      list: docEntityList(dataStore),
      callback: (entity) => {
        appStore.$router.push(getDocLink(entity));
      },
    } satisfies CommandEntitySection<Doc>,
    {
      label: "Views",
      list: viewEntityList(dataStore),
      callback: (entity) => {
        appStore.$router.push(getViewLink(entity));
      },
    } satisfies CommandEntitySection<View>,
    {
      label: "Dashboards",
      list: dashboardEntityList(dataStore),
      callback: (entity) => {
        appStore.$router.push(getDashboardLink(entity));
      },
    } satisfies CommandEntitySection<Dashboard>,
    {
      label: "Dartboards",
      list: dartboardEntityList(dataStore),
      callback: (entity) => {
        appStore.$router.push(getDartboardLink(entity, dataStore.getSpaceByDuid));
      },
    } satisfies CommandEntitySection<Dartboard>,
    {
      label: "Folders",
      list: folderEntityList(dataStore),
      callback: (entity) => {
        appStore.$router.push(getFolderLink(entity));
      },
    } satisfies CommandEntitySection<Folder>,
    {
      label: "Help center articles",
      list: helpCenterArticleEntityList(dataStore),
      callback: (entity) => {
        window.open(entity.url, "_blank");
      },
    } satisfies CommandEntitySection<HelpCenterArticle>,
  ];
};

export const getCommandsMap = (commands: CommandSection[]): Map<CommandId, Command> =>
  new Map(commands.flatMap((e) => e.commands.map((command) => [command.id, command]) satisfies [CommandId, Command][]));

const convertKeyToStroke = (key: Key | KeyboardShortcutStroke, isMac: boolean): KeyboardShortcutStroke => {
  if ((typeof key === "object" && "kind" in key) || !(key in KeyDisplay)) {
    return key;
  }

  const mapping = KeyDisplay[key as Key];
  return isMac ? (mapping.mac ?? mapping.default) : mapping.default;
};
export const getStrokesForPlatform = (
  commands: CommandSection[],
  commandId: CommandId,
  isMac: boolean
): KeyboardShortcutStroke[] | undefined => {
  const command = getCommandsMap(commands).get(commandId);
  const shortcut = command?.shortcut;
  if (!shortcut) {
    return undefined;
  }

  if (shortcut.displayOverride) {
    return shortcut.displayOverride.map((e) => convertKeyToStroke(e, isMac));
  }

  const binding = isMac ? (shortcut.mac ?? shortcut.default) : shortcut.default;
  if (!binding) {
    return undefined;
  }

  const shortcuts = Array.isArray(binding) ? binding : [binding];
  return shortcuts.flatMap((e, index): KeyboardShortcutStroke[] => {
    const strokeKeys: Key[][] = e.then ? [e.keys, e.then] : [e.keys];

    const strokes = strokeKeys.flatMap((keys, i): KeyboardShortcutStroke[] => [
      ...(i > 0 ? [{ kind: KeyboardShortcutStrokeKind.SEPARATOR, content: "then" }] : []),
      ...keys.map((key) => convertKeyToStroke(key, isMac)),
    ]);

    return index > 0 ? [OR, ...strokes] : strokes;
  });
};
